import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import io from "socket.io-client";
import { getRuntimeApiBase, BUILT_REACT_APP_SOCKET_URL } from "./apiBase";
import ArcadePanel from "./ArcadePanel";
import EconomyHub from "./EconomyHub";
import ServerVoicePanel from "./ServerVoicePanel";
import {
  loadUnread,
  bumpChannelUnread,
  clearChannelUnread,
  clearGuildChannelUnreads,
  bumpDmUnread,
  clearDmUnread,
  sumGuildUnread,
  sumAllDmUnread,
  dmUnreadForPeer,
  formatUnreadBadge,
  SidebarUnreadPill,
  makeChannelUnreadKey,
} from "./unreadStore";

function IconDm(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconHome(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 22V12h6v10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TENOR_API_KEY = process.env.REACT_APP_TENOR_API_KEY || "LIVDSRZULELA";

const ADMIN_TOKEN_KEY = "chat_admin_token";
const SESSION_TOKEN_KEY = "chat_session_token";
const GUILD_ID_KEY = "chat_active_guild_id";
const SIDEBAR_COLLAPSED_KEY = "chat_sidebar_collapsed";
/** Matches chat-server guild-store DEFAULT_GUILD_ID */
const DEFAULT_HOME_GUILD_ID = "default";
const AUTH_USERNAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9._ -]{0,22}[A-Za-z0-9])?$/;
const AUTH_USERNAME_HELP =
  "Usernames can use letters, numbers, spaces, dots, underscores, and hyphens, up to 24 characters.";

function normalizeAuthUserName(raw) {
  if (typeof raw !== "string") return "";
  const name = raw.trim().replace(/\s+/g, " ");
  if (!name || name.length > 24 || !AUTH_USERNAME_RE.test(name)) return "";
  return name;
}

function readStoredGuildId() {
  try {
    const s = localStorage.getItem(GUILD_ID_KEY);
    return s && s.trim() ? s.trim() : "";
  } catch {
    return "";
  }
}
const THEME_KEY = "chat_theme_v1";
const ACCENT_KEY = "chat_accent_h";

const DEFAULT_CHANNEL_LIST = [
  { id: "general", label: "general" },
  { id: "announcements", label: "announcements" },
  { id: "gaming", label: "gaming" },
  { id: "music", label: "music" },
  { id: "media", label: "media" },
  { id: "tech", label: "tech" },
  { id: "random", label: "random" },
];

const DEFAULT_CHANNEL_IDS = DEFAULT_CHANNEL_LIST.map((c) => c.id);

function emptyMessagesByChannel() {
  return DEFAULT_CHANNEL_LIST.reduce((acc, { id }) => {
    acc[id] = [];
    return acc;
  }, {});
}

function emptyPinnedByChannel() {
  return DEFAULT_CHANNEL_LIST.reduce((acc, { id }) => {
    acc[id] = null;
    return acc;
  }, {});
}

function emptyTypingByChannel() {
  return DEFAULT_CHANNEL_LIST.reduce((acc, { id }) => {
    acc[id] = [];
    return acc;
  }, {});
}

function formatSmartTime(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function userHue(userName) {
  const s = userName || "";
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

/** @param {string} apiBase @param {string | null | undefined} iconUrl */
function resolveServerIconSrc(apiBase, iconUrl) {
  if (!iconUrl || typeof iconUrl !== "string") return null;
  const s = iconUrl.trim();
  if (!s) return null;
  if (s.startsWith("https://") || s.startsWith("http://")) return s;
  if (s.startsWith("/")) {
    const base = String(apiBase || "").replace(/\/$/, "");
    return `${base}${s}`;
  }
  return null;
}

function ServerPillFace({ apiBase, server }) {
  const [iconBroken, setIconBroken] = useState(false);
  const src = resolveServerIconSrc(apiBase, server.iconUrl);
  if (src && !iconBroken) {
    return (
      <img
        src={src}
        alt=""
        className="server-rail-pill-img"
        onError={() => setIconBroken(true)}
      />
    );
  }
  return <>{(server.name || "?").slice(0, 2).toUpperCase()}</>;
}

function userDotStyle(hue) {
  return { background: `hsl(${hue}, 65%, 50%)` };
}

function UserAvatar({ apiBase, userName, avatarUrl, size = 32 }) {
  const hue = userHue(userName);
  const dim = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    display: "block",
  };
  const [avatarBroken, setAvatarBroken] = useState(false);
  useEffect(() => {
    setAvatarBroken(false);
  }, [avatarUrl, userName]);
  if (avatarUrl && !avatarBroken) {
    return (
      <img
        src={`${apiBase}${avatarUrl}`}
        alt=""
        style={{ ...dim, objectFit: "cover" }}
        onError={() => setAvatarBroken(true)}
      />
    );
  }
  return <span style={{ ...dim, ...userDotStyle(hue) }} />;
}

/** @param {string} userName @param {object | null} serverConfig */
function memberRolesResolved(userName, serverConfig) {
  if (!userName || !serverConfig?.roles?.length) return [];
  const key = String(userName).toLowerCase();
  const ids = serverConfig.memberRoles?.[key];
  if (!Array.isArray(ids) || !ids.length) return [];
  const byId = new Map(serverConfig.roles.map((r) => [r.id, r]));
  const out = ids.map((id) => byId.get(id)).filter(Boolean);
  return out.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function RolePills({ roles, max, style }) {
  if (!roles?.length) return null;
  const list = max != null ? roles.slice(0, max) : roles;
  const extra = max != null && roles.length > max ? roles.length - max : 0;
  return (
    <span style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", ...style }}>
      {list.map((r) => (
        <span
          key={r.id}
          title={r.name}
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.02,
            padding: "3px 8px",
            borderRadius: 4,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            background: `${r.color || "#5865F2"}26`,
            color: r.color || "#5865F2",
            border: `1px solid ${r.color || "#5865F2"}66`,
          }}
        >
          {r.name}
        </span>
      ))}
      {extra > 0 ? (
        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--app-muted)" }}>+{extra}</span>
      ) : null}
    </span>
  );
}

const URL_PATTERN = /(https?:\/\/[^\s<]+[^<.,)\]\s])/gi;

function lineWithUrls(text) {
  if (!text) return null;
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    if (/^https?:\/\//i.test(part)) {
      const isYt = /youtube\.com|youtu\.be/i.test(part);
      return (
        <React.Fragment key={`u-${i}`}>
          <a href={part} target="_blank" rel="noopener noreferrer" style={{ color: "var(--app-accent)" }}>
            {isYt ? "▶ YouTube link" : part}
          </a>
        </React.Fragment>
      );
    }
    return <React.Fragment key={`t-${i}`}>{part}</React.Fragment>;
  });
}

function lineWithMentions(text, onlineLower, accentClass) {
  if (!text) return null;
  const mentionRe = /(@[^\s@]+)/g;
  const parts = text.split(mentionRe);
  return parts.map((part, i) => {
    if (part.startsWith("@")) {
      const handle = part.slice(1).toLowerCase();
      const hit = onlineLower.has(handle);
      return (
        <span
          key={`m-${i}`}
          className={hit ? accentClass : undefined}
          style={
            hit
              ? {
                  background: "var(--app-mention-bg)",
                  borderRadius: 4,
                  padding: "0 4px",
                }
              : undefined
          }
        >
          {part}
        </span>
      );
    }
    return <React.Fragment key={`p-${i}`}>{lineWithUrls(part)}</React.Fragment>;
  });
}

function loadStoredTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    const acc = localStorage.getItem(ACCENT_KEY);
    return {
      dark: raw ? raw === "dark" : true,
      accentHue: acc != null ? Math.min(360, Math.max(0, Number(acc) || 235)) : 235,
    };
  } catch {
    return { dark: true, accentHue: 235 };
  }
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const themeInit = loadStoredTheme();
  const apiBase = useMemo(() => getRuntimeApiBase(), []);
  const deployApiMissing = useMemo(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") return false;
    return !BUILT_REACT_APP_SOCKET_URL;
  }, []);

  const apiHostname = useMemo(() => {
    try {
      return new URL(apiBase).hostname;
    } catch {
      return "";
    }
  }, [apiBase]);

  const apiPointsToSameHostAsPage =
    typeof window !== "undefined" &&
    apiHostname.length > 0 &&
    apiHostname === window.location.hostname;
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem(SESSION_TOKEN_KEY) || "");
  const [userName, setUserName] = useState("");
  const [authTab, setAuthTab] = useState("login");
  const [authUser, setAuthUser] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authPass2, setAuthPass2] = useState("");
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [inviteSignupMode, setInviteSignupMode] = useState(false);
  const [signupUsernames, setSignupUsernames] = useState(null);
  const [signupOptionsLoaded, setSignupOptionsLoaded] = useState(false);
  const [signupOptionsError, setSignupOptionsError] = useState("");
  const [signupOptionsReloadKey, setSignupOptionsReloadKey] = useState(0);
  const [authInviteCode, setAuthInviteCode] = useState("");

  const [darkMode, setDarkMode] = useState(themeInit.dark);
  const [accentHue, setAccentHue] = useState(themeInit.accentHue);

  const [activeNav, setActiveNav] = useState("chat");
  const [activeChannel, setActiveChannel] = useState("general");
  const [messagesByChannel, setMessagesByChannel] = useState(emptyMessagesByChannel);
  const [pinnedByChannel, setPinnedByChannel] = useState(emptyPinnedByChannel);

  const [presence, setPresence] = useState([]);
  const [myAvatarUrl, setMyAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renamePass, setRenamePass] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameMsg, setRenameMsg] = useState("");
  const [message, setMessage] = useState("");
  const [chatNotice, setChatNotice] = useState("");
  const [connected, setConnected] = useState(false);
  const [arcadeSocialToast, setArcadeSocialToast] = useState(null);
  const [typingByChannel, setTypingByChannel] = useState(emptyTypingByChannel);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState("");
  const [gifPickerOpen, setGifPickerOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifResults, setGifResults] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);

  const [sharedTodos, setSharedTodos] = useState([]);
  const [newTodo, setNewTodo] = useState("");
  const [pollsState, setPollsState] = useState([]);
  const [pollFeedItems, setPollFeedItems] = useState([]);
  const [newPollQuestion, setNewPollQuestion] = useState("");
  const [newPollOptions, setNewPollOptions] = useState(["", ""]);
  const [ideaBoard, setIdeaBoard] = useState([]);
  const [newIdeaText, setNewIdeaText] = useState("");

  const [dmPeerList, setDmPeerList] = useState([]);
  const [dmMessagesByPeer, setDmMessagesByPeer] = useState({});
  const [activeDmPeer, setActiveDmPeer] = useState(null);
  const [dmInput, setDmInput] = useState("");
  const [dmStartInput, setDmStartInput] = useState("");

  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [statusDraft, setStatusDraft] = useState("");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("chat_sound") === "1");
  const [showJump, setShowJump] = useState(false);

  const [activeGuildId, setActiveGuildId] = useState(readStoredGuildId);
  const [serverDirectory, setServerDirectory] = useState([]);
  const [publicServersList, setPublicServersList] = useState([]);
  const [serversBusy, setServersBusy] = useState(false);
  const [serversErr, setServersErr] = useState("");
  const [newServerName, setNewServerName] = useState("");
  const [joinServerId, setJoinServerId] = useState("");
  const [createServerModalOpen, setCreateServerModalOpen] = useState(false);
  const [newServerVisibility, setNewServerVisibility] = useState("private");
  const [newServerIconUrl, setNewServerIconUrl] = useState("");
  const [ownerServerIconDraft, setOwnerServerIconDraft] = useState({});

  const [serverConfig, setServerConfig] = useState(null);
  const [newChannelSlug, setNewChannelSlug] = useState("");
  const [deleteChannelPick, setDeleteChannelPick] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleColor, setNewRoleColor] = useState("#5865F2");
  const [newRolePerms, setNewRolePerms] = useState({
    pin: false,
    kick: false,
    channels: false,
    roles: false,
    manageMessages: false,
    purge: false,
    announce: false,
  });
  const [modAnnounceDraft, setModAnnounceDraft] = useState("");
  const [modToolChannel, setModToolChannel] = useState("");
  const [assignRoleUser, setAssignRoleUser] = useState("");
  const [assignRoleId, setAssignRoleId] = useState("");
  const [sidebarAccountOpen, setSidebarAccountOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [profileModalUser, setProfileModalUser] = useState(null);
  const [dmSearch, setDmSearch] = useState("");
  const [unreadState, setUnreadState] = useState(() => ({ channels: {}, dms: {} }));

  const socketRef = useRef(null);
  const authBusyRef = useRef(false);
  const socketGuildIdRef = useRef(null);
  const channelIdsRef = useRef(DEFAULT_CHANNEL_IDS);
  const userMenuRef = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef(null);
  const profileAvatarInputRef = useRef(null);
  const serverIconFileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const dmScrollRef = useRef(null);
  const dmFileInputRef = useRef(null);
  const beepRef = useRef(null);
  const userNameRef = useRef("");
  const activeNavRef = useRef("chat");
  const activeChannelRef = useRef("general");
  const activeGuildIdRef = useRef("");
  const activeDmPeerRef = useRef(null);

  useEffect(() => {
    try {
      if (sessionToken) localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
      else localStorage.removeItem(SESSION_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/auth/restore`, { credentials: "include" });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!data?.token || cancelled) return;
        setSessionToken(data.token);
        if (data.userName) setUserName(data.userName);
        setMyAvatarUrl(data.avatarUrl || null);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, sessionToken]);

  useEffect(() => {
    if (sessionToken) return undefined;
    let cancelled = false;
    setSignupOptionsLoaded(false);
    setSignupOptionsError("");
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/auth/signup-usernames`, { credentials: "include" });
        if (!r.ok) throw new Error("Could not load signup options.");
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        const inv = Boolean(data.inviteSignup);
        setInviteSignupMode(inv);
        setSignupUsernames(inv && Array.isArray(data.usernames) ? data.usernames : inv ? [] : null);
      } catch {
        if (!cancelled) {
          setInviteSignupMode(false);
          setSignupUsernames(null);
          setSignupOptionsError("Could not load signup options. Check your connection and try again.");
        }
      } finally {
        if (!cancelled) setSignupOptionsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, sessionToken, signupOptionsReloadKey]);

  useEffect(() => {
    userNameRef.current = userName;
  }, [userName]);

  useEffect(() => {
    activeNavRef.current = activeNav;
  }, [activeNav]);
  useEffect(() => {
    activeChannelRef.current = activeChannel;
  }, [activeChannel]);
  useEffect(() => {
    activeGuildIdRef.current = activeGuildId;
  }, [activeGuildId]);
  useEffect(() => {
    activeDmPeerRef.current = activeDmPeer;
  }, [activeDmPeer]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
    if (sidebarCollapsed) setSidebarAccountOpen(false);
  }, [sidebarCollapsed]);

  useEffect(() => {
    const u = (userName || "").toLowerCase();
    if (!u) setUnreadState({ channels: {}, dms: {} });
    else setUnreadState(loadUnread(u));
  }, [userName]);

  useEffect(() => {
    const ul = (userName || "").toLowerCase();
    if (!ul || activeNav !== "chat" || !activeGuildId || !activeChannel) return;
    clearChannelUnread(setUnreadState, ul, activeGuildId, activeChannel);
  }, [userName, activeNav, activeGuildId, activeChannel]);

  useEffect(() => {
    const ul = (userName || "").toLowerCase();
    if (!ul || activeNav !== "dms" || !activeDmPeer) return;
    clearDmUnread(setUnreadState, ul, activeDmPeer.toLowerCase());
  }, [userName, activeNav, activeDmPeer]);

  const accountMenuWasOpen = useRef(false);
  useEffect(() => {
    if (sidebarAccountOpen && !accountMenuWasOpen.current) {
      setRenameDraft(userName || "");
      setRenamePass("");
      setRenameMsg("");
    }
    accountMenuWasOpen.current = sidebarAccountOpen;
  }, [sidebarAccountOpen, userName]);

  useEffect(() => {
    if (!sidebarAccountOpen) return undefined;
    const onDoc = (e) => {
      const el = userMenuRef.current;
      if (el && !el.contains(e.target)) setSidebarAccountOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sidebarAccountOpen]);

  useEffect(() => {
    if (!profileModalUser) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setProfileModalUser(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profileModalUser]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem(ACCENT_KEY, String(accentHue));
  }, [accentHue]);

  useEffect(() => {
    localStorage.setItem("chat_sound", soundOn ? "1" : "0");
  }, [soundOn]);

  useEffect(() => {
    return () => {
      if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
    };
  }, [pendingImagePreviewUrl]);

  const refreshServerDirectory = useCallback(async () => {
    if (!sessionToken) return;
    const adminTokForMerge =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(ADMIN_TOKEN_KEY) || "" : "";
    setServersBusy(true);
    setServersErr("");
    try {
      const r = await fetch(`${apiBase}/api/servers/mine`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || "Could not load servers.");
      }
      const data = await r.json();
      let list = Array.isArray(data.servers) ? [...data.servers] : [];
      if (adminTokForMerge) {
        const ra = await fetch(`${apiBase}/api/admin/servers`, {
          headers: {
            Authorization: `Bearer ${adminTokForMerge}`,
            "Content-Type": "application/json",
          },
        });
        if (ra.ok) {
          const ad = await ra.json();
          const extra = Array.isArray(ad.servers) ? ad.servers : [];
          const map = new Map(list.map((s) => [s.id, { ...s }]));
          for (const s of extra) {
            if (!map.has(s.id)) map.set(s.id, { ...s, allServersVisible: true });
          }
          list = [...map.values()];
        }
      }
      list.sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
      setServerDirectory(list);
    } catch (e) {
      setServersErr(e?.message || "Failed to load servers.");
    } finally {
      setServersBusy(false);
    }
    try {
      const rp = await fetch(`${apiBase}/api/servers/public`);
      if (rp.ok) {
        const pub = await rp.json();
        setPublicServersList(Array.isArray(pub.servers) ? pub.servers : []);
      }
    } catch {
      /* ignore */
    }
  }, [sessionToken, apiBase]);

  useEffect(() => {
    if (sessionToken) refreshServerDirectory();
  }, [sessionToken, refreshServerDirectory]);

  const websiteAdminToken = useMemo(() => {
    try {
      return sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
    } catch {
      return "";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally invalidate cached token (e.g. after /admin)
  }, [location.pathname, sessionToken]);

  const playSoftBeep = useCallback(() => {
    if (!soundOn) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = beepRef.current || new Ctx();
      beepRef.current = ctx;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + 0.1);
    } catch {
      /* ignore */
    }
  }, [soundOn]);

  const maybeNotify = useCallback(
    (payload) => {
      if (!payload || payload.sender === userName) return;
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      if (!document.hidden) return;
      const prev = payload.text?.slice(0, 80) || (payload.imageUrl ? "Image" : payload.gifUrl ? "GIF" : "Message");
      try {
        new Notification(`${payload.sender}`, { body: prev });
      } catch {
        /* ignore */
      }
    },
    [userName]
  );

  const appendMessages = useCallback((channel, updater) => {
    setMessagesByChannel((prev) => ({
      ...prev,
      [channel]: updater(prev[channel] || []),
    }));
  }, []);

  useEffect(() => {
    if (!sessionToken) {
      setConnected(false);
      setServerConfig(null);
      channelIdsRef.current = DEFAULT_CHANNEL_IDS;
      socketGuildIdRef.current = null;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return;
    }

    let handshakeGuildId = "";
    try {
      const g = localStorage.getItem(GUILD_ID_KEY);
      if (g && g.trim()) handshakeGuildId = g.trim();
    } catch {
      /* ignore */
    }

    const socket = io(apiBase, {
      auth: {
        token: sessionToken,
        ...(handshakeGuildId ? { guildId: handshakeGuildId } : {}),
        ...(websiteAdminToken ? { websiteAdminToken: websiteAdminToken } : {}),
      },
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    const appliesToGuild = (payload) => {
      const gid = payload && payload.guildId;
      if (!gid) return true;
      return gid === socketGuildIdRef.current;
    };

    const acceptChatPayload = (payload) => {
      if (!payload?.id) return false;
      if (payload.kind === "chat") {
        const hasText = typeof payload.text === "string" && payload.text.length > 0;
        const hasImg = typeof payload.imageUrl === "string" && payload.imageUrl.length > 0;
        const hasGif = typeof payload.gifUrl === "string" && payload.gifUrl.length > 0;
        return hasText || hasImg || hasGif;
      }
      if (payload.kind === "emote") {
        return typeof payload.text === "string";
      }
      return false;
    };

    const onChatMessage = (payload) => {
      if (!appliesToGuild(payload)) return;
      if (!acceptChatPayload(payload)) return;
      const ch = payload.channel;
      if (!ch) return;
      appendMessages(ch, (arr) => [...arr, payload]);
      const gid = payload.guildId || socketGuildIdRef.current;
      const ul = (userNameRef.current || "").toLowerCase();
      if (ul && gid && payload.sender && payload.sender !== userNameRef.current) {
        const viewing =
          activeNavRef.current === "chat" &&
          activeChannelRef.current === ch &&
          activeGuildIdRef.current === gid;
        if (!viewing) {
          bumpChannelUnread(setUnreadState, ul, gid, ch);
        }
      }
      maybeNotify(payload);
      playSoftBeep();
    };

    const onMessageReaction = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      const messageId = payload?.messageId;
      const emoji = payload?.emoji;
      const by = payload?.by;
      if (!ch || typeof messageId !== "string" || typeof emoji !== "string" || typeof by !== "string") return;
      setMessagesByChannel((prev) => ({
        ...prev,
        [ch]: (prev[ch] || []).map((item) => {
          if (item.id !== messageId || (item.kind !== "chat" && item.kind !== "emote")) {
            return item;
          }
          const reactions = item.reactions ? { ...item.reactions } : {};
          const group = reactions[emoji] ? [...reactions[emoji]] : [];
          if (!group.includes(by)) group.push(by);
          reactions[emoji] = group;
          return { ...item, reactions };
        }),
      }));
    };

    const onSystemMessage = (payload) => {
      if (!appliesToGuild(payload)) return;
      if (!payload?.id || typeof payload?.text !== "string") return;
      if (payload.channel === "*") {
        setMessagesByChannel((prev) => {
          const next = { ...prev };
          channelIdsRef.current.forEach((id) => {
            next[id] = [...(next[id] || []), payload];
          });
          return next;
        });
      } else if (payload.channel) {
        appendMessages(payload.channel, (arr) => [...arr, payload]);
      }
    };

    const onPresence = (payload) => {
      if (!appliesToGuild(payload)) return;
      const incoming = Array.isArray(payload?.users) ? payload.users : [];
      setPresence(incoming);
    };

    const onChannelState = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      if (!ch) return;
      const msgs = Array.isArray(payload?.messages) ? payload.messages : [];
      setMessagesByChannel((prev) => ({ ...prev, [ch]: msgs }));
      setPinnedByChannel((prev) => ({ ...prev, [ch]: payload.pinned ?? null }));
      setActiveChannel(ch);
    };

    const onMessageEdited = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      const messageId = payload?.messageId;
      if (!ch || !messageId) return;
      setMessagesByChannel((prev) => ({
        ...prev,
        [ch]: (prev[ch] || []).map((m) =>
          m.id === messageId ? { ...m, text: payload.text, editedAt: payload.editedAt } : m
        ),
      }));
    };

    const onMessageDeleted = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      const messageId = payload?.messageId;
      if (!ch || !messageId) return;
      setMessagesByChannel((prev) => ({
        ...prev,
        [ch]: (prev[ch] || []).filter((m) => m.id !== messageId),
      }));
    };

    const onPinnedUpdate = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      if (!ch) return;
      setPinnedByChannel((prev) => ({ ...prev, [ch]: payload.pinned ?? null }));
    };

    const onTodoState = (payload) => {
      if (!appliesToGuild(payload)) return;
      setSharedTodos(Array.isArray(payload?.todos) ? payload.todos : []);
    };
    const onPollState = (payload) => {
      if (!appliesToGuild(payload)) return;
      setPollsState(Array.isArray(payload?.polls) ? payload.polls : []);
    };
    const onPollFeedState = (payload) => {
      if (!appliesToGuild(payload)) return;
      setPollFeedItems(Array.isArray(payload?.items) ? payload.items : []);
    };
    const onIdeaBoardState = (payload) => {
      if (!appliesToGuild(payload)) return;
      setIdeaBoard(Array.isArray(payload?.ideas) ? payload.ideas : []);
    };

    const onTypingUpdate = (payload) => {
      if (!appliesToGuild(payload)) return;
      const ch = payload?.channel;
      const incoming = Array.isArray(payload?.users) ? payload.users : [];
      if (!ch) return;
      setTypingByChannel((prev) => ({ ...prev, [ch]: incoming }));
    };

    const onChatError = (payload) => {
      if (!payload?.message) return;
      if (payload.code === "kicked") {
        try {
          localStorage.removeItem(GUILD_ID_KEY);
        } catch {
          /* ignore */
        }
        setActiveGuildId("");
        socketGuildIdRef.current = null;
        setMessagesByChannel(emptyMessagesByChannel());
        setPinnedByChannel(emptyPinnedByChannel());
        setActiveNav("chat");
        setActiveChannel("general");
      }
      if (payload.code === "banned") {
        try {
          localStorage.removeItem(SESSION_TOKEN_KEY);
          localStorage.removeItem(GUILD_ID_KEY);
        } catch {
          /* ignore */
        }
        setSessionToken("");
        setActiveGuildId("");
        socketGuildIdRef.current = null;
        setUserName("");
        setMyAvatarUrl(null);
        setDmPeerList([]);
        setDmMessagesByPeer({});
        setActiveDmPeer(null);
        setDmInput("");
        setDmStartInput("");
        setMessagesByChannel(emptyMessagesByChannel());
        setPinnedByChannel(emptyPinnedByChannel());
        setActiveNav("chat");
        setActiveChannel("general");
      }
      if (payload.code === "server_deleted" && payload.guildId) {
        void refreshServerDirectory();
        const gid = payload.guildId;
        if (socketGuildIdRef.current === gid || activeGuildIdRef.current === gid) {
          try {
            localStorage.setItem(GUILD_ID_KEY, DEFAULT_HOME_GUILD_ID);
          } catch {
            /* ignore */
          }
          const sock = socketRef.current;
          if (sock?.connected) {
            sock.emit("guild_switch", { guildId: DEFAULT_HOME_GUILD_ID });
          }
          setActiveGuildId(DEFAULT_HOME_GUILD_ID);
          socketGuildIdRef.current = DEFAULT_HOME_GUILD_ID;
          setActiveNav("chat");
          setActiveChannel("general");
        }
      }
      if (payload.code === "mod_ok") {
        setChatNotice(payload.message);
        setTimeout(() => setChatNotice(""), 6000);
        return;
      }
      setChatNotice(payload.message);
      setTimeout(() => setChatNotice(""), 5000);
    };

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    const onSessionOk = (p) => {
      if (p?.guildId) {
        socketGuildIdRef.current = p.guildId;
        setActiveGuildId(p.guildId);
        try {
          localStorage.setItem(GUILD_ID_KEY, p.guildId);
        } catch {
          /* ignore */
        }
      }
      if (p?.userName) setUserName(p.userName);
      setMyAvatarUrl(p?.avatarUrl || null);
    };

    const onConnectError = (err) => {
      const msg = err?.message || "";
      if (/Unauthorized|unauthorized/i.test(msg)) {
        setChatNotice("Session expired or reset. Sign in again with your username and password — your account is still there.");
        setSessionToken("");
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    };

    const onChatCleared = (payload) => {
      if (payload?.guildId && payload.guildId !== socketGuildIdRef.current) return;
      const single = typeof payload?.channel === "string" ? payload.channel.trim() : "";
      const ul = (userNameRef.current || "").toLowerCase();
      const gid = socketGuildIdRef.current;
      if (single && channelIdsRef.current.includes(single)) {
        setMessagesByChannel((prev) => ({ ...prev, [single]: [] }));
        setPinnedByChannel((prev) => ({ ...prev, [single]: null }));
        if (ul && gid) clearChannelUnread(setUnreadState, ul, gid, single);
        return;
      }
      const ids = channelIdsRef.current;
      setMessagesByChannel((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = [];
        });
        return next;
      });
      setPinnedByChannel((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = null;
        });
        return next;
      });
      if (ul && gid) clearGuildChannelUnreads(setUnreadState, ul, gid);
    };

    const onServerConfig = (payload) => {
      if (!payload?.channels?.length) return;
      if (payload.guildId) {
        socketGuildIdRef.current = payload.guildId;
        setActiveGuildId(payload.guildId);
        try {
          localStorage.setItem(GUILD_ID_KEY, payload.guildId);
        } catch {
          /* ignore */
        }
      }
      setServerConfig(payload);
      const ids = payload.channels.map((c) => c.id);
      channelIdsRef.current = ids;
      setMessagesByChannel((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          if (!next[id]) next[id] = [];
        }
        return next;
      });
      setPinnedByChannel((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          if (!(id in next)) next[id] = null;
        }
        return next;
      });
      setTypingByChannel((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          if (!next[id]) next[id] = [];
        }
        return next;
      });
      setActiveChannel((prev) => {
        if (ids.includes(prev)) return prev;
        const fb = ids.includes("general") ? "general" : ids[0] || "general";
        setTimeout(() => {
          const sk = socketRef.current;
          if (sk?.connected) sk.emit("channel_switch", { channel: fb });
        }, 0);
        return fb;
      });
    };

    const onDmSummary = (payload) => {
      setDmPeerList(Array.isArray(payload?.peers) ? payload.peers : []);
    };

    const onDmState = (payload) => {
      const peer = payload?.peer;
      if (!peer) return;
      setActiveDmPeer(peer);
      const msgs = Array.isArray(payload?.messages) ? payload.messages : [];
      setDmMessagesByPeer((prev) => ({ ...prev, [peer]: msgs }));
    };

    const onDmMessage = (payload) => {
      if (payload?.kind !== "dm_chat") return;
      const me = userNameRef.current;
      const other = payload.sender === me ? payload.recipient : payload.sender;
      if (!other) return;
      setDmMessagesByPeer((prev) => ({
        ...prev,
        [other]: [...(prev[other] || []), payload],
      }));
      if (payload.sender !== me) {
        const ul = (userNameRef.current || "").toLowerCase();
        const pl = String(other).toLowerCase();
        if (ul) {
          const viewingDm =
            activeNavRef.current === "dms" &&
            activeDmPeerRef.current &&
            String(activeDmPeerRef.current).toLowerCase() === pl;
          if (!viewingDm) bumpDmUnread(setUnreadState, ul, pl);
        }
        if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
          try {
            new Notification(`${payload.sender} (DM)`, {
              body: (payload.text || "").slice(0, 80) || "Attachment",
            });
          } catch {
            /* ignore */
          }
        }
        playSoftBeep();
      }
    };

    socket.on("chat_message", onChatMessage);
    socket.on("system_message", onSystemMessage);
    socket.on("presence_update", onPresence);
    socket.on("channel_state", onChannelState);
    socket.on("message_reaction", onMessageReaction);
    socket.on("message_edited", onMessageEdited);
    socket.on("message_deleted", onMessageDeleted);
    socket.on("pinned_update", onPinnedUpdate);
    socket.on("todo_state", onTodoState);
    socket.on("poll_state", onPollState);
    socket.on("poll_feed_state", onPollFeedState);
    socket.on("idea_board_state", onIdeaBoardState);
    socket.on("typing_update", onTypingUpdate);
    socket.on("chat_error", onChatError);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("session_ok", onSessionOk);
    socket.on("connect_error", onConnectError);
    socket.on("chat_cleared", onChatCleared);
    socket.on("server_config", onServerConfig);
    socket.on("dm_summary", onDmSummary);
    socket.on("dm_state", onDmState);
    socket.on("dm_message", onDmMessage);
    function onArcadeFeed(evt) {
      if (!evt || typeof evt !== "object") return;
      const name = evt.displayName || evt.userName || "Someone";
      let msg = "";
      if (evt.type === "level_up") msg = `${name} ranked up · street rep Lv ${evt.level}`;
      else if (evt.type === "daily_trifecta") msg = `${name} cleared the crew contract on Neon row`;
      else return;
      setArcadeSocialToast(msg);
      window.setTimeout(() => setArcadeSocialToast(null), 6500);
    }
    socket.on("arcade_feed", onArcadeFeed);

    return () => {
      socket.off("chat_message", onChatMessage);
      socket.off("system_message", onSystemMessage);
      socket.off("presence_update", onPresence);
      socket.off("channel_state", onChannelState);
      socket.off("message_reaction", onMessageReaction);
      socket.off("message_edited", onMessageEdited);
      socket.off("message_deleted", onMessageDeleted);
      socket.off("pinned_update", onPinnedUpdate);
      socket.off("todo_state", onTodoState);
      socket.off("poll_state", onPollState);
      socket.off("poll_feed_state", onPollFeedState);
      socket.off("idea_board_state", onIdeaBoardState);
      socket.off("typing_update", onTypingUpdate);
      socket.off("chat_error", onChatError);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("session_ok", onSessionOk);
      socket.off("connect_error", onConnectError);
      socket.off("chat_cleared", onChatCleared);
      socket.off("server_config", onServerConfig);
      socket.off("dm_summary", onDmSummary);
      socket.off("dm_state", onDmState);
      socket.off("dm_message", onDmMessage);
      socket.off("arcade_feed", onArcadeFeed);
      socketRef.current = null;
      socket.close();
    };
  }, [
    sessionToken,
    apiBase,
    websiteAdminToken,
    appendMessages,
    maybeNotify,
    playSoftBeep,
    setUnreadState,
    refreshServerDirectory,
  ]);

  const switchChannel = (ch) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("channel_switch", { channel: ch });
  };

  const switchToGuild = useCallback((guildId) => {
    const socket = socketRef.current;
    if (!socket?.connected || !guildId) return;
    if (guildId === socketGuildIdRef.current) return;
    socket.emit("guild_switch", { guildId });
    setActiveGuildId(guildId);
    try {
      localStorage.setItem(GUILD_ID_KEY, guildId);
    } catch {
      /* ignore */
    }
  }, []);

  const createServerFromUi = async () => {
    const name = newServerName.trim();
    if (!name || !sessionToken) return;
    setServersBusy(true);
    setServersErr("");
    try {
      const r = await fetch(`${apiBase}/api/servers/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          visibility: newServerVisibility === "public" ? "public" : "private",
          ...(newServerIconUrl.trim() ? { iconUrl: newServerIconUrl.trim() } : {}),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setServersErr(data.error || "Could not create server.");
        return;
      }
      setNewServerName("");
      setNewServerIconUrl("");
      setCreateServerModalOpen(false);
      await refreshServerDirectory();
      if (data.serverId) switchToGuild(data.serverId);
      setActiveNav("chat");
    } catch {
      setServersErr("Network error.");
    } finally {
      setServersBusy(false);
    }
  };

  const joinServerFromUi = async () => {
    const sid = joinServerId.trim();
    if (!sid || !sessionToken) return;
    setServersBusy(true);
    setServersErr("");
    try {
      const r = await fetch(`${apiBase}/api/servers/join`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ serverId: sid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setServersErr(data.error || "Could not join server.");
        return;
      }
      setJoinServerId("");
      await refreshServerDirectory();
      switchToGuild(sid);
      setActiveNav("chat");
    } catch {
      setServersErr("Network error.");
    } finally {
      setServersBusy(false);
    }
  };

  const uploadServerIconFromFile = async (file) => {
    if (!file || !sessionToken || !activeGuildId) return;
    setServersBusy(true);
    setServersErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${apiBase}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setServersErr(data.error || "Upload failed.");
        return;
      }
      const url = data.url ? String(data.url) : "";
      if (url) {
        setOwnerServerIconDraft((prev) => ({ ...prev, [activeGuildId]: url }));
      }
    } catch {
      setServersErr("Network error.");
    } finally {
      setServersBusy(false);
      if (serverIconFileInputRef.current) serverIconFileInputRef.current.value = "";
    }
  };

  const saveOwnerServerIcon = async (serverId) => {
    if (!sessionToken || !serverId) return;
    const raw = ownerServerIconDraft[serverId];
    if (raw === undefined) return;
    setServersBusy(true);
    setServersErr("");
    try {
      const r = await fetch(`${apiBase}/api/servers/icon`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          guildId: serverId,
          iconUrl: String(raw).trim() === "" ? "" : String(raw).trim(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setServersErr(data.error || "Could not update server picture.");
        return;
      }
      setOwnerServerIconDraft((prev) => {
        const next = { ...prev };
        delete next[serverId];
        return next;
      });
      await refreshServerDirectory();
    } catch {
      setServersErr("Network error.");
    } finally {
      setServersBusy(false);
    }
  };

  const createChannelFromUi = () => {
    const socket = socketRef.current;
    if (!socket?.connected || !newChannelSlug.trim()) return;
    socket.emit("channel_create", { name: newChannelSlug.trim().toLowerCase() });
    setNewChannelSlug("");
  };

  const deleteChannelFromUi = () => {
    const socket = socketRef.current;
    if (!socket?.connected || !deleteChannelPick) return;
    if (deleteChannelPick === "general") return;
    if (!window.confirm(`Delete channel #${deleteChannelPick}?`)) return;
    socket.emit("channel_delete", { channelId: deleteChannelPick });
    setDeleteChannelPick("");
  };

  const createRoleFromUi = () => {
    const socket = socketRef.current;
    if (!socket?.connected || !newRoleName.trim()) return;
    socket.emit("role_create", {
      name: newRoleName.trim(),
      color: newRoleColor,
      permissions: newRolePerms,
    });
    setNewRoleName("");
  };

  const deleteRoleById = (roleId) => {
    const socket = socketRef.current;
    if (!socket?.connected || !roleId) return;
    if (!window.confirm("Delete this role?")) return;
    socket.emit("role_delete", { roleId });
  };

  const assignRoleFromUi = () => {
    const socket = socketRef.current;
    if (!socket?.connected || !assignRoleUser.trim() || !assignRoleId) return;
    socket.emit("role_assign", { userName: assignRoleUser.trim(), roleId: assignRoleId });
  };

  const unassignRoleFromUi = () => {
    const socket = socketRef.current;
    if (!socket?.connected || !assignRoleUser.trim() || !assignRoleId) return;
    socket.emit("role_unassign", { userName: assignRoleUser.trim(), roleId: assignRoleId });
    setAssignRoleId("");
  };

  const modKickUser = (targetUserName) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    if (!window.confirm(`Kick ${targetUserName} from the server?`)) return;
    socket.emit("mod_kick", { userName: targetUserName });
  };

  const modPurgeSelectedChannel = (ch) => {
    const socket = socketRef.current;
    const id = String(ch || "").trim();
    if (!socket?.connected || !id) return;
    if (!window.confirm(`Delete all messages in #${id} for everyone?`)) return;
    socket.emit("mod_purge_channel", { channel: id });
  };

  const modSendAnnounceForChannel = (ch) => {
    const socket = socketRef.current;
    const id = String(ch || "").trim();
    if (!socket?.connected || !id) return;
    const text = modAnnounceDraft.trim();
    if (!text) {
      setChatNotice("Enter announcement text.");
      setTimeout(() => setChatNotice(""), 4000);
      return;
    }
    socket.emit("mod_announce", { text, channel: id });
    setModAnnounceDraft("");
  };

  const displayPeerLabel = useCallback((peer) => {
    if (!peer) return "";
    const hit = presence.find(
      (p) => p.userName === peer || p.userName.toLowerCase() === peer.toLowerCase()
    );
    if (hit) return hit.displayName || hit.userName;
    return peer;
  }, [presence]);

  const mergedDmPeers = useMemo(() => {
    const s = new Set(dmPeerList);
    presence.forEach((p) => {
      if (p.userName !== userName) s.add(p.userName);
    });
    return [...s].sort((a, b) => displayPeerLabel(a).localeCompare(displayPeerLabel(b)));
  }, [dmPeerList, presence, userName, displayPeerLabel]);

  const openDmWith = useCallback(
    (peer) => {
      if (!peer) return;
      if (peer === userName || peer.toLowerCase() === userName.toLowerCase()) return;
      setActiveNav("dms");
      setActiveDmPeer(peer);
      const ul = (userName || "").toLowerCase();
      if (ul) clearDmUnread(setUnreadState, ul, peer.toLowerCase());
      socketRef.current?.emit("dm_join", { peer });
    },
    [userName, setUnreadState]
  );

  const startNewDm = () => {
    const raw = dmStartInput.trim();
    if (!raw) return;
    openDmWith(raw);
    setDmStartInput("");
  };

  const sendDmMessage = () => {
    const text = dmInput.trim();
    if (!text || !activeDmPeer) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("dm_message", { peer: activeDmPeer, text });
    setDmInput("");
  };

  const sendDmImageMessage = async (file) => {
    if (!file || !activeDmPeer) return;
    setUploadBusy(true);
    setChatNotice("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${apiBase}/api/upload`, {
        method: "POST",
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setChatNotice(data.error || "Upload failed.");
        return;
      }
      socketRef.current?.emit("dm_message", {
        peer: activeDmPeer,
        text: dmInput.trim() || "",
        imageUrl: data.url,
      });
      setDmInput("");
    } catch {
      setChatNotice("Could not send image.");
    } finally {
      setUploadBusy(false);
      if (dmFileInputRef.current) dmFileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (activeNav !== "dms") return;
    const el = dmScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [activeNav, activeDmPeer, dmMessagesByPeer]);

  const sendTypingState = (isTyping) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    if (isTypingRef.current === isTyping) return;
    isTypingRef.current = isTyping;
    socket.emit("typing", { isTyping });
  };

  const pushStatus = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("profile_status", { status: statusDraft.trim() });
  };

  const sendMessage = () => {
    const text = message.trim();
    if (editingMessage) {
      const socket = socketRef.current;
      if (!socket?.connected) return;
      socket.emit("edit_message", {
        channel: activeChannel,
        messageId: editingMessage.id,
        text,
      });
      setEditingMessage(null);
      setMessage("");
      sendTypingState(false);
      return;
    }
    if (!text) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const reply =
      replyTo && replyTo.id
        ? {
            id: replyTo.id,
            sender: replyTo.sender,
            text: (replyTo.text || "").slice(0, 200),
          }
        : undefined;
    socket.emit("message", reply ? { text, replyTo: reply } : text);
    setMessage("");
    setReplyTo(null);
    sendTypingState(false);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  };

  const sendImageMessage = async (file) => {
    if (!file) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    setUploadBusy(true);
    setChatNotice("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${apiBase}/api/upload`, {
        method: "POST",
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setChatNotice(data.error || "Image upload failed.");
        return;
      }
      const caption = message.trim();
      const reply =
        replyTo && replyTo.id
          ? { id: replyTo.id, sender: replyTo.sender, text: (replyTo.text || "").slice(0, 200) }
          : undefined;
      socket.emit(
        "message",
        reply
          ? { text: caption || "", imageUrl: data.url, replyTo: reply }
          : { text: caption || "", imageUrl: data.url }
      );
      setMessage("");
      setReplyTo(null);
      if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
      setPendingImagePreviewUrl("");
      setPendingImageFile(null);
      sendTypingState(false);
    } catch {
      setChatNotice("Could not upload image.");
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const searchGifs = async (query) => {
    const q = query.trim();
    if (!q) {
      setGifResults([]);
      return;
    }
    setGifLoading(true);
    try {
      const url = `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(
        q
      )}&key=${encodeURIComponent(TENOR_API_KEY)}&client_key=chattest&limit=12&media_filter=gif&contentfilter=medium`;
      const r = await fetch(url);
      const data = await r.json();
      const items = Array.isArray(data?.results) ? data.results : [];
      const mapped = items
        .map((it) => {
          const mf = it?.media_formats || {};
          const gif = mf.gif || mf.mediumgif || mf.tinygif;
          return gif?.url ? { id: it.id, url: gif.url } : null;
        })
        .filter(Boolean);
      setGifResults(mapped);
    } catch {
      setChatNotice("GIF search failed.");
    } finally {
      setGifLoading(false);
    }
  };

  const sendGifMessage = (gifUrl) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const caption = message.trim();
    const reply =
      replyTo && replyTo.id
        ? { id: replyTo.id, sender: replyTo.sender, text: (replyTo.text || "").slice(0, 200) }
        : undefined;
    socket.emit(
      "message",
      reply ? { text: caption || "", gifUrl, replyTo: reply } : { text: caption || "", gifUrl }
    );
    setMessage("");
    setReplyTo(null);
    setGifPickerOpen(false);
    setGifQuery("");
    setGifResults([]);
  };

  const queueImageForUpload = (file) => {
    if (!file) return;
    if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
    setPendingImageFile(file);
    setPendingImagePreviewUrl(URL.createObjectURL(file));
  };

  const cancelPendingImage = () => {
    setPendingImageFile(null);
    if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
    setPendingImagePreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sendReaction = (messageId, emoji) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("react", { messageId, emoji });
  };

  const deleteMessage = (messageId) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("delete_message", { channel: activeChannel, messageId });
  };

  const pinMessage = (messageId) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("pin_message", { messageId });
  };

  const unpinChannel = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("unpin_message", { channel: activeChannel });
  };

  const exportChat = () => {
    const blob = new Blob([JSON.stringify(messagesByChannel, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `chat-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const register = async () => {
    if (authBusyRef.current) return;
    const username = normalizeAuthUserName(authUser);
    if (!username) {
      setAuthError(AUTH_USERNAME_HELP);
      return;
    }
    setAuthBusy(true);
    authBusyRef.current = true;
    setAuthError("");
    if (authPass !== authPass2) {
      setAuthError("Passwords do not match.");
      setAuthBusy(false);
      authBusyRef.current = false;
      return;
    }
    if (inviteSignupMode && !/^\d{4}$/.test(authInviteCode.trim())) {
      setAuthError("Enter the 4-digit invitation code for the selected username.");
      setAuthBusy(false);
      authBusyRef.current = false;
      return;
    }
    try {
      const body = {
        username,
        password: authPass,
        ...(inviteSignupMode && signupUsernames?.length ? { inviteCode: authInviteCode.trim() } : {}),
      };
      const r = await fetch(`${apiBase}/api/auth/register`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const rawText = await r.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      if (!r.ok) {
        const hint =
          data.error ||
          (rawText && rawText.trim().startsWith("<")
            ? `Server returned HTML (${r.status}) — check REACT_APP_SOCKET_URL points to your API, not the Pages site.`
            : rawText
              ? `HTTP ${r.status}: ${rawText.slice(0, 180)}${rawText.length > 180 ? "…" : ""}`
            : `Registration failed (HTTP ${r.status}).`);
        setAuthError(hint);
        return;
      }
      localStorage.setItem(SESSION_TOKEN_KEY, data.token);
      setSessionToken(data.token);
      setUserName(data.userName);
      setMyAvatarUrl(data.avatarUrl || null);
      setAuthPass("");
      setAuthPass2("");
      setAuthInviteCode("");
    } catch (e) {
      setAuthError(
        e?.message?.includes("Failed to fetch")
          ? "Could not reach the API (network/CORS/wrong URL). Confirm REACT_APP_SOCKET_URL and redeploy, or set localStorage CHAT_API_BASE."
          : "Network error."
      );
    } finally {
      authBusyRef.current = false;
      setAuthBusy(false);
    }
  };

  const authUserNameReady = Boolean(normalizeAuthUserName(authUser));
  const registerSubmitDisabled =
    authBusy ||
    !signupOptionsLoaded ||
    Boolean(signupOptionsError) ||
    authPass.length < 4 ||
    authPass !== authPass2 ||
    !authUserNameReady ||
    (inviteSignupMode &&
      (!signupUsernames?.length || !/^\d{4}$/.test(authInviteCode.trim())));

  const login = async () => {
    if (authBusyRef.current) return;
    const username = normalizeAuthUserName(authUser);
    if (!username) {
      setAuthError(AUTH_USERNAME_HELP);
      return;
    }
    setAuthBusy(true);
    authBusyRef.current = true;
    setAuthError("");
    try {
      const r = await fetch(`${apiBase}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password: authPass }),
      });
      const rawText = await r.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      if (!r.ok) {
        const hint =
          data.error ||
          (rawText && rawText.trim().startsWith("<")
            ? `Server returned HTML (${r.status}) — API base URL is probably wrong.`
            : rawText
              ? `HTTP ${r.status}: ${rawText.slice(0, 180)}${rawText.length > 180 ? "…" : ""}`
            : `Login failed (HTTP ${r.status}).`);
        setAuthError(hint);
        return;
      }
      localStorage.setItem(SESSION_TOKEN_KEY, data.token);
      setSessionToken(data.token);
      setUserName(data.userName);
      setMyAvatarUrl(data.avatarUrl || null);
      setAuthPass("");
    } catch (e) {
      setAuthError(
        e?.message?.includes("Failed to fetch")
          ? "Could not reach the API. Check REACT_APP_SOCKET_URL / CHAT_API_BASE and CORS on the server."
          : "Network error."
      );
    } finally {
      authBusyRef.current = false;
      setAuthBusy(false);
    }
  };

  const loginSubmitDisabled = authBusy || !authUserNameReady || !authPass.trim();

  const submitAuthForm = (e) => {
    e.preventDefault();
    if (authTab === "register") {
      if (!registerSubmitDisabled) register();
      return;
    }
    if (!loginSubmitDisabled) login();
  };

  const logout = async () => {
    let tok = sessionToken;
    if (!tok) {
      try {
        tok = localStorage.getItem(SESSION_TOKEN_KEY) || "";
      } catch {
        tok = "";
      }
    }
    if (tok) {
      try {
        await fetch(`${apiBase}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${tok}` },
        });
      } catch {
        /* still clear client state */
      }
    }
    localStorage.removeItem(SESSION_TOKEN_KEY);
    try {
      localStorage.removeItem(GUILD_ID_KEY);
    } catch {
      /* ignore */
    }
    setSessionToken("");
    setActiveGuildId("");
    socketGuildIdRef.current = null;
    setUserName("");
    setMyAvatarUrl(null);
    setDmPeerList([]);
    setDmMessagesByPeer({});
    setActiveDmPeer(null);
    setDmInput("");
    setDmStartInput("");
  };

  const uploadProfileAvatar = async (file) => {
    if (!file || !sessionToken) return;
    setAvatarBusy(true);
    setChatNotice("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${apiBase}/api/profile/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: fd,
      });
      const rawText = await r.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      if (!r.ok) {
        setChatNotice(data.error || "Could not update profile picture.");
        setTimeout(() => setChatNotice(""), 5000);
        return;
      }
      setMyAvatarUrl(data.avatarUrl || null);
    } catch {
      setChatNotice("Could not update profile picture.");
      setTimeout(() => setChatNotice(""), 5000);
    } finally {
      setAvatarBusy(false);
      if (profileAvatarInputRef.current) profileAvatarInputRef.current.value = "";
    }
  };

  const addTodo = () => {
    const socket = socketRef.current;
    const text = newTodo.trim();
    if (!socket?.connected || !text) return;
    socket.emit("todo_add", { text });
    setNewTodo("");
  };

  const toggleTodo = (id) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("todo_toggle", { id });
  };

  const removeTodo = (id) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("todo_remove", { id });
  };

  const createPoll = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const options = newPollOptions.map((o) => o.trim()).filter(Boolean);
    socket.emit("poll_create", { question: newPollQuestion.trim(), options });
    setNewPollQuestion("");
    setNewPollOptions(["", ""]);
  };

  const votePoll = (pollId, optionId) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("poll_vote", { pollId, optionId });
  };

  const addIdea = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const text = newIdeaText.trim();
    if (!text) return;
    socket.emit("idea_add", { text });
    setNewIdeaText("");
  };

  const voteIdea = (ideaId) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("idea_vote", { ideaId });
  };

  const removeIdea = (ideaId) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("idea_remove", { ideaId });
  };

  const onChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJump(dist > 120);
  };

  const scrollChatBottom = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    if (activeNav !== "chat") return;
    scrollChatBottom();
  }, [activeNav, activeChannel]);

  const channelsForUi = useMemo(
    () => (serverConfig?.channels?.length ? serverConfig.channels : DEFAULT_CHANNEL_LIST),
    [serverConfig]
  );

  const currentServerMeta = useMemo(
    () => serverDirectory.find((s) => s.id === activeGuildId) || null,
    [serverDirectory, activeGuildId]
  );

  /** Owner or server admin — can change server picture (API + My servers list). Home server included when you are a server admin. */
  const canManageServerPicture = useMemo(() => {
    if (!activeGuildId) return false;
    const meta = currentServerMeta;
    if (!meta) return false;
    if (meta.canManageIcon === true) return true;
    const me = (userName || "").toLowerCase();
    const ow = (meta.owner || "").toLowerCase();
    return Boolean(me && ow && me === ow);
  }, [activeGuildId, currentServerMeta, userName]);

  const serverSettingsIconDraftVal = useMemo(() => {
    if (!activeGuildId) return "";
    return ownerServerIconDraft[activeGuildId] !== undefined
      ? ownerServerIconDraft[activeGuildId]
      : currentServerMeta?.iconUrl || "";
  }, [activeGuildId, ownerServerIconDraft, currentServerMeta]);

  const serverSettingsIconPreviewSrc = useMemo(
    () => resolveServerIconSrc(apiBase, serverSettingsIconDraftVal || currentServerMeta?.iconUrl),
    [apiBase, serverSettingsIconDraftVal, currentServerMeta?.iconUrl]
  );

  const membershipIds = useMemo(() => new Set(serverDirectory.map((s) => s.id)), [serverDirectory]);

  const publicServerIds = useMemo(() => new Set(publicServersList.map((s) => s.id)), [publicServersList]);

  const railPublicServers = useMemo(
    () =>
      publicServersList
        .filter((s) => s.id !== DEFAULT_HOME_GUILD_ID)
        .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id))),
    [publicServersList]
  );

  const railPrivateServers = useMemo(
    () =>
      serverDirectory
        .filter((s) => s.id !== DEFAULT_HOME_GUILD_ID && !publicServerIds.has(s.id))
        .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id))),
    [serverDirectory, publicServerIds]
  );

  const serverHue = useCallback((id) => {
    let h = 0;
    const s = String(id || "");
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
  }, []);

  const dmRailBadgeLabel = useMemo(() => formatUnreadBadge(sumAllDmUnread(unreadState)), [unreadState]);
  const homeRailBadgeLabel = useMemo(
    () => formatUnreadBadge(sumGuildUnread(unreadState, DEFAULT_HOME_GUILD_ID)),
    [unreadState]
  );

  const openDmNav = useCallback(() => {
    setActiveNav("dms");
    setSidebarAccountOpen(false);
  }, []);

  const openHomeNav = useCallback(() => {
    setSidebarAccountOpen(false);
    setActiveNav("chat");
    switchToGuild(DEFAULT_HOME_GUILD_ID);
  }, [switchToGuild]);

  const openRailServer = useCallback(
    async (serverId) => {
      if (!serverId || !sessionToken) return;
      setSidebarAccountOpen(false);
      setActiveNav("chat");
      if (membershipIds.has(serverId)) {
        switchToGuild(serverId);
        return;
      }
      setServersBusy(true);
      setChatNotice("");
      try {
        const r = await fetch(`${apiBase}/api/servers/join`, {
          method: "POST",
          headers: { Authorization: `Bearer ${sessionToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ serverId }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          setChatNotice(data.error || "Could not join server.");
          setTimeout(() => setChatNotice(""), 5000);
          return;
        }
        await refreshServerDirectory();
        switchToGuild(serverId);
      } catch {
        setChatNotice("Network error joining server.");
        setTimeout(() => setChatNotice(""), 5000);
      } finally {
        setServersBusy(false);
      }
    },
    [sessionToken, apiBase, membershipIds, switchToGuild, refreshServerDirectory]
  );

  const myPerms = serverConfig?.my?.permissions || {
    pin: false,
    kick: false,
    channels: false,
    roles: false,
    manageMessages: false,
    purge: false,
    announce: false,
  };

  const isUserServerAdmin = useCallback(
    (name) =>
      !!serverConfig?.serverAdmins?.some((a) => String(a).toLowerCase() === String(name || "").toLowerCase()),
    [serverConfig]
  );

  const presenceByUserName = useMemo(() => {
    const m = new Map();
    presence.forEach((p) => m.set(p.userName, p));
    return m;
  }, [presence]);

  const filteredDmPeers = useMemo(() => {
    const q = dmSearch.trim().toLowerCase();
    if (!q) return mergedDmPeers;
    return mergedDmPeers.filter(
      (p) => p.toLowerCase().includes(q) || displayPeerLabel(p).toLowerCase().includes(q)
    );
  }, [mergedDmPeers, dmSearch, displayPeerLabel]);

  const openUserProfile = useCallback((name) => {
    if (!name) return;
    setProfileModalUser(String(name));
  }, []);

  const currentMessages = messagesByChannel[activeChannel] || [];
  const currentPinned = pinnedByChannel[activeChannel];
  const visibleTyping = (typingByChannel[activeChannel] || []).filter((u) => u !== userName);

  const onlineLower = new Set(presence.map((p) => (p.displayName || p.userName || "").toLowerCase()));

  const avatarByUserName = useMemo(() => {
    const m = new Map();
    presence.forEach((p) => m.set(p.userName, p.avatarUrl || null));
    if (userName) m.set(userName, myAvatarUrl ?? m.get(userName) ?? null);
    return m;
  }, [presence, userName, myAvatarUrl]);

  const shellStyle = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--app-bg)",
    color: "var(--app-text)",
    "--accent-h": accentHue,
  };

  const sidebarBtn = (active) => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "10px 14px",
    marginBottom: 3,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    background: active ? "var(--app-sidebar-hover)" : "transparent",
    color: "var(--app-text)",
    fontWeight: active ? 600 : 400,
    transition: "background 0.12s ease",
  });

  const collapsedNavButton = (label, title, nav, onClick) => (
    <button
      key={title}
      type="button"
      title={title}
      aria-label={title}
      onClick={
        onClick ||
        (() => {
          setActiveNav(nav);
          setSidebarAccountOpen(false);
        })
      }
      style={{
        width: 38,
        height: 38,
        borderRadius: 8,
        border: "1px solid var(--app-border)",
        background: activeNav === nav ? "var(--app-accent)" : "var(--app-input-bg)",
        color: activeNav === nav ? "#fff" : "var(--app-text)",
        cursor: "pointer",
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 8,
      }}
    >
      {label}
    </button>
  );

  if (!sessionToken) {
    const showConnectionHelp = deployApiMissing || apiPointsToSameHostAsPage;
    return (
      <div
        className={`app-shell auth-gate ${darkMode ? "" : "app-theme-light"}`}
        style={{ ...shellStyle, minHeight: "100dvh" }}
      >
        <div className="auth-gate-inner">
          <div className="auth-gate-hero">
            <h1>Triple T</h1>
            <p>Hang out in channels, DMs, polls, and shared boards. Sign up with a username or your host's invitation code.</p>
          </div>
          <div className="auth-gate-panel">
            <form className="auth-gate-card" onSubmit={submitAuthForm}>
              <div className="auth-gate-tabs" role="tablist" aria-label="Account">
                <button
                  type="button"
                  role="tab"
                  aria-selected={authTab === "login"}
                  data-active={authTab === "login"}
                  disabled={authBusy}
                  onClick={() => {
                    setAuthTab("login");
                    setAuthError("");
                  }}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={authTab === "register"}
                  data-active={authTab === "register"}
                  disabled={authBusy}
                  onClick={() => {
                    setAuthTab("register");
                    setAuthError("");
                    setAuthInviteCode("");
                  }}
                >
                  Register
                </button>
              </div>

              {showConnectionHelp ? (
                <details className="auth-gate-help">
                  <summary>Can&apos;t connect? Hosting setup</summary>
                  <div className="auth-gate-help-body">
                    {deployApiMissing ? (
                      <div
                        style={{
                          background: "rgba(240,71,71,0.12)",
                          border: "1px solid #f04747",
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: apiPointsToSameHostAsPage ? 12 : 0,
                          lineHeight: 1.45,
                        }}
                      >
                        <strong>Backend URL not baked into this build.</strong> In Cloudflare Pages add{" "}
                        <code style={{ wordBreak: "break-all" }}>REACT_APP_SOCKET_URL</code> = your API origin (no trailing
                        slash), e.g. <code>https://your-service.onrender.com</code>, then <strong>rebuild</strong> the site. On
                        Render, set <code>CORS_ORIGIN</code> to your exact Pages URL (or use the server&apos;s default{" "}
                        <code>*.pages.dev</code> allowlist).
                      </div>
                    ) : null}
                    {apiPointsToSameHostAsPage ? (
                      <div
                        style={{
                          background: "rgba(240,171,71,0.15)",
                          border: "1px solid #faa61a",
                          borderRadius: 8,
                          padding: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        API host matches this site&apos;s host — requests may hit your static host (404 on <code>/api/…</code>
                        ), not your API. Set <code>REACT_APP_SOCKET_URL</code> to your API origin and rebuild, or run{" "}
                        <code style={{ wordBreak: "break-all" }}>
                          localStorage.setItem(&quot;CHAT_API_BASE&quot;,&quot;https://your-api.example.com&quot;);location.reload()
                        </code>
                      </div>
                    ) : null}
                  </div>
                </details>
              ) : null}

              {authTab === "login" ? (
                <>
                  <label className="auth-gate-field" htmlFor="auth-username">
                    Username
                  </label>
                  <input
                    id="auth-username"
                    className="auth-gate-input"
                    autoComplete="username"
                    value={authUser}
                    onChange={(e) => {
                      setAuthUser(e.target.value);
                      if (authError === AUTH_USERNAME_HELP) setAuthError("");
                    }}
                    maxLength={24}
                  />
                </>
              ) : !signupOptionsLoaded ? (
                <p className="auth-gate-inline-note">Loading signup options...</p>
              ) : signupOptionsError ? (
                <div className="auth-gate-alert" role="alert">
                  <span>{signupOptionsError}</span>
                  <button type="button" onClick={() => setSignupOptionsReloadKey((n) => n + 1)}>
                    Retry
                  </button>
                </div>
              ) : inviteSignupMode ? (
                signupUsernames?.length ? (
                  <>
                    <label className="auth-gate-field" htmlFor="auth-signup-user">
                      Choose username
                    </label>
                    <select
                      id="auth-signup-user"
                      className="auth-gate-input"
                      autoComplete="username"
                      value={authUser}
                      onChange={(e) => {
                        setAuthUser(e.target.value);
                        setAuthInviteCode("");
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <option value="">Select a username…</option>
                      {signupUsernames.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <label className="auth-gate-field" htmlFor="auth-invite-code">
                      Invitation code
                    </label>
                    <input
                      id="auth-invite-code"
                      className="auth-gate-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={4}
                      placeholder="4-digit code"
                      aria-invalid={authInviteCode.length > 0 && authInviteCode.length < 4}
                      value={authInviteCode}
                      onChange={(e) => setAuthInviteCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    />
                    <p style={{ fontSize: 12, color: "var(--app-muted)", margin: "0 0 14px", lineHeight: 1.45 }}>
                      Pick the username you were given, then enter its matching code. Codes are checked on the server.
                    </p>
                  </>
                ) : (
                  <p className="auth-gate-error" role="alert">
                    No invitations are available right now. Try again later or contact the host.
                  </p>
                )
              ) : (
                <>
                  <label className="auth-gate-field" htmlFor="auth-username">
                    Username
                  </label>
                  <input
                    id="auth-username"
                    className="auth-gate-input"
                    autoComplete="username"
                    value={authUser}
                    onChange={(e) => {
                      setAuthUser(e.target.value);
                      if (authError === AUTH_USERNAME_HELP) setAuthError("");
                    }}
                    maxLength={24}
                  />
                  <p className="auth-gate-inline-note">{AUTH_USERNAME_HELP}</p>
                </>
              )}
              <label className="auth-gate-field" htmlFor="auth-password">
                Password
              </label>
              <input
                id="auth-password"
                className="auth-gate-input"
                type="password"
                autoComplete={authTab === "register" ? "new-password" : "current-password"}
                value={authPass}
                onChange={(e) => setAuthPass(e.target.value)}
              />
              {authTab === "register" ? (
                <>
                  <label className="auth-gate-field" htmlFor="auth-password2">
                    Confirm password
                  </label>
                  <input
                    id="auth-password2"
                    className="auth-gate-input"
                    type="password"
                    autoComplete="new-password"
                    value={authPass2}
                    onChange={(e) => setAuthPass2(e.target.value)}
                  />
                </>
              ) : null}
              {authError ? (
                <p className="auth-gate-error" role="alert">
                  {authError}
                </p>
              ) : null}
              <button
                type="submit"
                className="auth-gate-submit"
                disabled={authTab === "register" ? registerSubmitDisabled : loginSubmitDisabled}
              >
                {authBusy ? "Please wait..." : authTab === "register" ? "Create account" : "Sign in"}
              </button>
              <div
                style={{
                  marginTop: 22,
                  paddingTop: 20,
                  borderTop: "1px solid var(--app-border)",
                  fontSize: 13,
                  color: "var(--app-muted)",
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <input type="checkbox" checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
                  Dark theme
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ minWidth: "7rem" }}>Accent color</span>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    value={accentHue}
                    onChange={(e) => setAccentHue(Number(e.target.value))}
                    style={{ flex: "1 1 140px" }}
                  />
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{accentHue}°</span>
                </label>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell app-shell-main ${darkMode ? "" : "app-theme-light"}`} style={shellStyle}>
      <div className="app-main-layout">
        <nav className="server-rail" aria-label="Servers and direct messages">
          <div className="server-rail-top">
            <button
              type="button"
              className="server-rail-btn"
              title="Direct messages"
              aria-label={
                dmRailBadgeLabel ? `Direct messages, ${dmRailBadgeLabel.replace("+", " plus")} unread` : "Direct messages"
              }
              data-active={activeNav === "dms"}
              onClick={openDmNav}
            >
              <IconDm />
              {dmRailBadgeLabel ? (
                <span className="server-rail-unread" aria-hidden>
                  {dmRailBadgeLabel}
                </span>
              ) : null}
            </button>
            <div className="server-rail-sep" aria-hidden />
            <button
              type="button"
              className="server-rail-btn"
              title="Home — default server"
              aria-label={
                homeRailBadgeLabel
                  ? `Home server, ${homeRailBadgeLabel.replace("+", " plus")} unread`
                  : "Home server"
              }
              data-active={activeNav !== "dms" && activeGuildId === DEFAULT_HOME_GUILD_ID}
              onClick={openHomeNav}
            >
              <IconHome />
              {homeRailBadgeLabel ? (
                <span className="server-rail-unread" aria-hidden>
                  {homeRailBadgeLabel}
                </span>
              ) : null}
            </button>
            <div className="server-rail-sep" aria-hidden />
          </div>
          <div className="server-rail-scroll">
            {railPublicServers.length ? (
              <>
                <div className="server-rail-hint">Public</div>
                {railPublicServers.map((s) => {
                  const srvBadge = formatUnreadBadge(sumGuildUnread(unreadState, s.id));
                  return (
                    <button
                      key={`pub-${s.id}`}
                      type="button"
                      className="server-rail-btn server-rail-pill"
                      title={`${s.name} — public${membershipIds.has(s.id) ? "" : " (tap to join)"}`}
                      aria-label={
                        srvBadge
                          ? `${s.name}, ${srvBadge.replace("+", " plus")} unread`
                          : s.name
                      }
                      data-active={activeNav !== "dms" && activeGuildId === s.id}
                      style={{
                        background: `hsl(${serverHue(s.id)}, 42%, 44%)`,
                        color: "#fff",
                      }}
                      onClick={() => openRailServer(s.id)}
                    >
                      <ServerPillFace apiBase={apiBase} server={s} />
                      {srvBadge ? (
                        <span className="server-rail-unread" aria-hidden>
                          {srvBadge}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </>
            ) : null}
            {railPrivateServers.length ? (
              <>
                {railPublicServers.length ? <div className="server-rail-sep" aria-hidden /> : null}
                <div className="server-rail-hint">Private</div>
                {railPrivateServers.map((s) => {
                  const srvBadge = formatUnreadBadge(sumGuildUnread(unreadState, s.id));
                  return (
                    <button
                      key={`prv-${s.id}`}
                      type="button"
                      className="server-rail-btn server-rail-pill"
                      title={s.name}
                      aria-label={
                        srvBadge ? `${s.name}, ${srvBadge.replace("+", " plus")} unread` : s.name
                      }
                      data-active={activeNav !== "dms" && activeGuildId === s.id}
                      style={{
                        background: `hsl(${serverHue(s.id)}, 35%, 38%)`,
                        color: "#fff",
                      }}
                      onClick={() => openRailServer(s.id)}
                    >
                      <ServerPillFace apiBase={apiBase} server={s} />
                      {srvBadge ? (
                        <span className="server-rail-unread" aria-hidden>
                          {srvBadge}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </>
            ) : null}
          </div>
          <div className="server-rail-footer">
            <button
              type="button"
              className="server-rail-btn server-rail-btn--add"
              title="Create a server"
              aria-label="Create a server"
              onClick={() => {
                setServersErr("");
                setCreateServerModalOpen(true);
                setNewServerName("");
                setNewServerIconUrl("");
                setNewServerVisibility("private");
              }}
            >
              +
            </button>
          </div>
        </nav>

        {/* Channel / DM list */}
        <aside
          className="app-channel-sidebar"
          style={{
            width: sidebarCollapsed ? 56 : 240,
            flexShrink: 0,
            background: "var(--app-sidebar)",
            borderRight: "1px solid var(--app-border)",
            display: "flex",
            flexDirection: "column",
            padding: sidebarCollapsed ? 8 : 12,
            minHeight: 0,
            height: "100%",
            transition: "width 0.18s ease, padding 0.18s ease",
          }}
        >
          <button
            type="button"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!sidebarCollapsed}
            onClick={() => setSidebarCollapsed((v) => !v)}
            style={{
              width: sidebarCollapsed ? 40 : "100%",
              minHeight: 34,
              marginBottom: 10,
              borderRadius: 8,
              border: "1px solid var(--app-border)",
              background: "var(--app-input-bg)",
              color: "var(--app-text)",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            {sidebarCollapsed ? "›" : "‹ Collapse"}
          </button>
          {sidebarCollapsed ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
              {collapsedNavButton("#", "Current channel", "chat")}
              {collapsedNavButton("N", "Neon row", "arcade")}
              {collapsedNavButton("$", "The pit", "economy")}
              {collapsedNavButton("+", "Add a server", "servers", () => {
                setActiveNav("servers");
                refreshServerDirectory();
              })}
            </div>
          ) : activeNav === "dms" ? (
            <>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, letterSpacing: -0.2, flexShrink: 0 }}>
                Direct messages
              </div>
              <div style={{ fontSize: 11, color: "var(--app-muted)", marginBottom: 10, lineHeight: 1.4, flexShrink: 0 }}>
                Chats with other members
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", marginBottom: 8, marginLeft: -4, marginRight: -4 }}>
                <div style={{ padding: "0 4px 8px" }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    <input
                      value={dmStartInput}
                      onChange={(e) => setDmStartInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && startNewDm()}
                      placeholder="Username…"
                      disabled={!connected}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid var(--app-border)",
                        background: "var(--app-input-bg)",
                        color: "var(--app-text)",
                      }}
                    />
                    <button
                      type="button"
                      onClick={startNewDm}
                      disabled={!connected}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "none",
                        fontWeight: 600,
                        fontSize: 12,
                        background: "var(--app-accent)",
                        color: "#fff",
                        cursor: connected ? "pointer" : "default",
                        opacity: connected ? 1 : 0.5,
                        flexShrink: 0,
                      }}
                    >
                      Go
                    </button>
                  </div>
                  <input
                    value={dmSearch}
                    onChange={(e) => setDmSearch(e.target.value)}
                    placeholder="Search…"
                    style={{
                      width: "100%",
                      fontSize: 12,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-input-bg)",
                      color: "var(--app-text)",
                      marginBottom: 8,
                    }}
                  />
                </div>
                <div style={{ padding: "0 4px" }}>
                  {mergedDmPeers.length === 0 ? (
                    <p style={{ fontSize: 12, color: "var(--app-muted)", margin: 8, lineHeight: 1.45 }}>
                      No conversations yet. Enter a username above or use <strong>Message</strong> on a profile.
                    </p>
                  ) : filteredDmPeers.length === 0 ? (
                    <p style={{ fontSize: 12, color: "var(--app-muted)", margin: 8, textAlign: "center" }}>
                      No matches.
                    </p>
                  ) : (
                    filteredDmPeers.map((peer) => {
                      const active = activeDmPeer === peer;
                      const av = avatarByUserName.get(peer);
                      const dun = dmUnreadForPeer(unreadState, peer.toLowerCase());
                      return (
                        <button
                          key={peer}
                          type="button"
                          onClick={() => openDmWith(peer)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            width: "100%",
                            padding: "8px 10px",
                            marginBottom: 4,
                            border: "none",
                            borderRadius: 8,
                            cursor: "pointer",
                            textAlign: "left",
                            background: active ? "var(--app-sidebar-hover)" : "transparent",
                            color: "var(--app-text)",
                          }}
                        >
                          <UserAvatar apiBase={apiBase} userName={peer} avatarUrl={av} size={36} />
                          <div
                            style={{
                              minWidth: 0,
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: dun > 0 ? 700 : 600,
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                              }}
                            >
                              {displayPeerLabel(peer)}
                            </div>
                            <SidebarUnreadPill count={dun} />
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4, letterSpacing: 0.5, flexShrink: 0 }}>TRIPLE T</div>
              {currentServerMeta ? (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--app-muted)",
                    marginBottom: 10,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={currentServerMeta.id}
                >
                  {currentServerMeta.name}
                </div>
              ) : activeGuildId ? (
                <div style={{ fontSize: 11, color: "var(--app-muted)", marginBottom: 10 }} title={activeGuildId}>
                  Server
                </div>
              ) : null}
              <ServerVoicePanel
                socket={socketRef.current}
                connected={connected}
                serverName={currentServerMeta?.name || "Current server"}
              />
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "var(--app-muted)", marginBottom: 6 }}>
                  Text channels
                </div>
                {channelsForUi.map(({ id, label }) => {
                  const activeCh = activeNav === "chat" && activeChannel === id;
                  const chUnread =
                    unreadState.channels[makeChannelUnreadKey(activeGuildId || "", id)] || 0;
                  return (
                    <button
                      key={id}
                      type="button"
                      style={{
                        ...sidebarBtn(activeCh),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        fontWeight: chUnread > 0 ? 700 : activeCh ? 600 : 400,
                      }}
                      onClick={() => {
                        setActiveNav("chat");
                        setSidebarAccountOpen(false);
                        switchChannel(id);
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        # {label}
                      </span>
                      <SidebarUnreadPill count={chUnread} />
                    </button>
                  );
                })}
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "var(--app-muted)", margin: "14px 0 6px" }}>
                  Community
                </div>
                <button
                  type="button"
                  style={sidebarBtn(activeNav === "polls")}
                  onClick={() => {
                    setActiveNav("polls");
                    setSidebarAccountOpen(false);
                  }}
                >
                  📊 Poll feed
                </button>
                <button
                  type="button"
                  style={sidebarBtn(activeNav === "boards")}
                  onClick={() => {
                    setActiveNav("boards");
                    setSidebarAccountOpen(false);
                  }}
                >
                  📋 Boards
                </button>
                <button
                  type="button"
                  style={sidebarBtn(activeNav === "arcade")}
                  onClick={() => {
                    setActiveNav("arcade");
                    setSidebarAccountOpen(false);
                  }}
                >
                  Neon row
                </button>
                <button
                  type="button"
                  style={sidebarBtn(activeNav === "economy")}
                  onClick={() => {
                    setActiveNav("economy");
                    setSidebarAccountOpen(false);
                  }}
                >
                  The pit
                </button>
                <button
                  type="button"
                  style={sidebarBtn(activeNav === "servers")}
                  onClick={() => {
                    setActiveNav("servers");
                    setSidebarAccountOpen(false);
                    refreshServerDirectory();
                  }}
                >
                  Add a server…
                </button>
                {myPerms.channels || myPerms.roles || canManageServerPicture ? (
                  <button
                    type="button"
                    style={sidebarBtn(activeNav === "server-settings")}
                    onClick={() => {
                      setActiveNav("server-settings");
                      setSidebarAccountOpen(false);
                    }}
                  >
                    ⚙ Server settings
                  </button>
                ) : null}
              </div>
            </>
          )}

          {!sidebarCollapsed ? (
            <>
              <input
                ref={profileAvatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadProfileAvatar(f);
                }}
              />
              <div
                ref={userMenuRef}
                style={{
                  flexShrink: 0,
                  paddingTop: 8,
                  borderTop: "1px solid var(--app-border)",
                  position: "relative",
                }}
              >
            <button
              type="button"
              onClick={() => setSidebarAccountOpen((o) => !o)}
              aria-expanded={sidebarAccountOpen}
              aria-haspopup="true"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 6px",
                borderRadius: 8,
                border: "1px solid transparent",
                background: sidebarAccountOpen ? "var(--app-elevated)" : "transparent",
                color: "var(--app-text)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <UserAvatar apiBase={apiBase} userName={userName} avatarUrl={myAvatarUrl} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {userName}
                </div>
                <div style={{ fontSize: 10, color: connected ? "#3ba55d" : "#ed4245" }}>
                  {connected ? "Online" : "Connecting…"}
                </div>
              </div>
              <span style={{ fontSize: 10, color: "var(--app-muted)", flexShrink: 0 }}>{sidebarAccountOpen ? "▲" : "▼"}</span>
            </button>
            {sidebarAccountOpen ? (
              <div
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  right: 0,
                  marginBottom: 6,
                  maxHeight: "min(70vh, 440px)",
                  overflowY: "auto",
                  background: "var(--app-elevated)",
                  border: "1px solid var(--app-border)",
                  borderRadius: 10,
                  padding: 12,
                  boxShadow: "0 -8px 32px rgba(0,0,0,0.28)",
                  zIndex: 80,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: "var(--app-muted)", textTransform: "uppercase" }}>
                  Account
                </div>
                <button
                  type="button"
                  title="Change profile picture"
                  onClick={() => profileAvatarInputRef.current?.click()}
                  disabled={avatarBusy || !connected}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 10,
                    padding: 8,
                    borderRadius: 8,
                    border: "1px solid var(--app-border)",
                    background: "var(--app-input-bg)",
                    color: "var(--app-text)",
                    cursor: avatarBusy || !connected ? "default" : "pointer",
                  }}
                >
                  <UserAvatar apiBase={apiBase} userName={userName} avatarUrl={myAvatarUrl} size={28} />
                  <span style={{ fontSize: 13 }}>{avatarBusy ? "Uploading…" : "Change profile picture"}</span>
                </button>
                <div style={{ marginBottom: 10, fontSize: 12 }}>
                  <div style={{ color: "var(--app-muted)", marginBottom: 4 }}>Change username (re-logs sessions in place)</div>
                  <input
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    placeholder="New username"
                    style={{
                      width: "100%",
                      fontSize: 12,
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-input-bg)",
                      color: "var(--app-text)",
                      marginBottom: 6,
                      boxSizing: "border-box",
                    }}
                  />
                  <input
                    type="password"
                    value={renamePass}
                    onChange={(e) => setRenamePass(e.target.value)}
                    placeholder="Current password"
                    style={{
                      width: "100%",
                      fontSize: 12,
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-input-bg)",
                      color: "var(--app-text)",
                      marginBottom: 6,
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    type="button"
                    disabled={renameBusy || !renameDraft.trim() || !renamePass}
                    onClick={async () => {
                      setRenameBusy(true);
                      setRenameMsg("");
                      try {
                        const r = await fetch(`${apiBase}/api/profile/rename`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
                          body: JSON.stringify({ userName: renameDraft.trim(), password: renamePass }),
                        });
                        const text = await r.text();
                        let j = {};
                        try {
                          j = text ? JSON.parse(text) : {};
                        } catch {
                          throw new Error(text.slice(0, 100));
                        }
                        if (!r.ok) throw new Error(j.error || "Rename failed");
                        setUserName(j.userName);
                        setRenameDraft(j.userName);
                        setRenamePass("");
                        socketRef.current?.disconnect();
                        socketRef.current?.connect();
                        refreshServerDirectory();
                        setRenameMsg("Saved.");
                      } catch (e) {
                        setRenameMsg(e.message || "Failed");
                      } finally {
                        setRenameBusy(false);
                      }
                    }}
                    style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, cursor: "pointer" }}
                  >
                    {renameBusy ? "Saving..." : "Update username"}
                  </button>
                  {renameMsg ? (
                    <div style={{ marginTop: 4, color: renameMsg === "Saved." ? "#3ba55d" : "#f04747" }}>{renameMsg}</div>
                  ) : null}
                </div>
                <input
                  placeholder="Set a status…"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  onBlur={pushStatus}
                  onKeyDown={(e) => e.key === "Enter" && pushStatus()}
                  style={{
                    width: "100%",
                    fontSize: 12,
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid var(--app-border)",
                    background: "var(--app-input-bg)",
                    color: "var(--app-text)",
                    marginBottom: 10,
                    boxSizing: "border-box",
                  }}
                />
                <label style={{ fontSize: 12, color: "var(--app-muted)", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <input type="checkbox" checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} /> Dark theme
                </label>
                <label style={{ fontSize: 12, color: "var(--app-muted)", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  Accent
                  <input
                    type="range"
                    min={0}
                    max={360}
                    value={accentHue}
                    onChange={(e) => setAccentHue(Number(e.target.value))}
                    style={{ flex: 1 }}
                  />
                </label>
                <label style={{ fontSize: 12, color: "var(--app-muted)", display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} /> Message sounds
                </label>
                <button
                  type="button"
                  onClick={() => Notification.requestPermission()}
                  style={{ fontSize: 12, width: "100%", marginBottom: 8, padding: 8, borderRadius: 6, cursor: "pointer" }}
                >
                  Enable desktop alerts
                </button>
                <button
                  type="button"
                  onClick={exportChat}
                  style={{ fontSize: 12, width: "100%", marginBottom: 8, padding: 8, borderRadius: 6, cursor: "pointer" }}
                >
                  Export chat JSON
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSidebarAccountOpen(false);
                    navigate("/admin");
                  }}
                  style={{ fontSize: 12, width: "100%", marginBottom: 8, padding: 8, borderRadius: 6, cursor: "pointer" }}
                >
                  Global admin dashboard
                </button>
                <button
                  type="button"
                  onClick={logout}
                  style={{
                    fontSize: 12,
                    width: "100%",
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid var(--app-border)",
                    background: "transparent",
                    color: "var(--app-text)",
                    cursor: "pointer",
                  }}
                >
                  Log out
                </button>
              </div>
            ) : null}
              </div>
            </>
          ) : null}
        </aside>

        {/* Main */}
        <main style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", background: "var(--app-chat)" }}>
          {activeNav === "dms" ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                background: "var(--app-chat)",
              }}
            >
              {!activeDmPeer ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 32,
                      color: "var(--app-muted)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.25 }}>✉️</div>
                    <p style={{ margin: 0, fontSize: 15, maxWidth: 320, lineHeight: 1.55 }}>
                      Pick a conversation from the list, start one with a username, or use <strong>Message</strong> on a profile.
                    </p>
                  </div>
                ) : (
                  <>
                    <header
                      style={{
                        padding: "14px 20px",
                        borderBottom: "1px solid var(--app-border)",
                        background: "var(--app-elevated)",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexShrink: 0,
                      }}
                    >
                      <UserAvatar
                        apiBase={apiBase}
                        userName={activeDmPeer}
                        avatarUrl={avatarByUserName.get(activeDmPeer)}
                        size={48}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button
                          type="button"
                          onClick={() => openUserProfile(activeDmPeer)}
                          style={{
                            display: "block",
                            padding: 0,
                            border: "none",
                            background: "none",
                            font: "inherit",
                            fontWeight: 700,
                            fontSize: 17,
                            color: "var(--app-text)",
                            cursor: "pointer",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {displayPeerLabel(activeDmPeer)}
                        </button>
                        <div style={{ marginTop: 6 }}>
                          <RolePills roles={memberRolesResolved(activeDmPeer, serverConfig)} />
                        </div>
                        {isUserServerAdmin(activeDmPeer) ? (
                          <span
                            style={{
                              display: "inline-block",
                              marginTop: 6,
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: 0.06,
                              padding: "3px 8px",
                              borderRadius: 4,
                              background: "rgba(240, 178, 50, 0.2)",
                              color: "#f0b232",
                              border: "1px solid rgba(240, 178, 50, 0.45)",
                            }}
                          >
                            SERVER ADMIN
                          </span>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => openUserProfile(activeDmPeer)}
                        style={{
                          fontSize: 13,
                          padding: "8px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--app-border)",
                          background: "var(--app-input-bg)",
                          color: "var(--app-text)",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        Profile
                      </button>
                    </header>
                    {chatNotice ? (
                      <div
                        role="status"
                        style={{
                          margin: "8px 16px 0",
                          padding: "10px 14px",
                          borderRadius: 8,
                          background: "rgba(240, 71, 71, 0.12)",
                          border: "1px solid rgba(240, 71, 71, 0.32)",
                          color: "#f04747",
                          fontSize: 14,
                        }}
                      >
                        {chatNotice}
                      </div>
                    ) : null}
                    <div
                      ref={dmScrollRef}
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "16px 20px 20px",
                        minHeight: 0,
                      }}
                    >
                      {(dmMessagesByPeer[activeDmPeer] || []).map((item) => {
                        const mine = item.sender === userName;
                        return (
                          <div
                            key={item.id}
                            style={{
                              marginBottom: 12,
                              fontSize: 14,
                              display: "flex",
                              justifyContent: mine ? "flex-end" : "flex-start",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: mine ? "row-reverse" : "row",
                                gap: 10,
                                alignItems: "flex-end",
                                maxWidth: "min(100%, 520px)",
                              }}
                            >
                              <UserAvatar
                                apiBase={apiBase}
                                userName={item.sender}
                                avatarUrl={avatarByUserName.get(item.sender)}
                                size={36}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    borderRadius: 14,
                                    padding: "10px 14px",
                                    background: mine ? "var(--app-accent)" : "var(--app-elevated)",
                                    color: mine ? "#fff" : "var(--app-text)",
                                    border: mine ? "none" : "1px solid var(--app-border)",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 11,
                                      opacity: mine ? 0.95 : 1,
                                      marginBottom: 4,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      flexWrap: "wrap",
                                      color: mine ? "rgba(255,255,255,0.9)" : "var(--app-muted)",
                                    }}
                                  >
                                    {!mine ? (
                                      <button
                                        type="button"
                                        onClick={() => openUserProfile(item.sender)}
                                        style={{
                                          padding: 0,
                                          border: "none",
                                          background: "none",
                                          fontWeight: 700,
                                          fontSize: 12,
                                          color: "inherit",
                                          cursor: "pointer",
                                        }}
                                      >
                                        {item.sender}
                                      </button>
                                    ) : (
                                      <span style={{ fontWeight: 700 }}>You</span>
                                    )}
                                    <span>{formatSmartTime(item.timestamp)}</span>
                                  </div>
                                  {item.text ? (
                                    <div style={{ lineHeight: 1.45 }}>{lineWithMentions(item.text, onlineLower)}</div>
                                  ) : null}
                                  {item.imageUrl ? (
                                    <div style={{ marginTop: 8 }}>
                                      <img
                                        src={`${apiBase}${item.imageUrl}`}
                                        alt=""
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: 280,
                                          borderRadius: 8,
                                          display: "block",
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                  {item.gifUrl ? (
                                    <div style={{ marginTop: 8 }}>
                                      <img
                                        src={item.gifUrl}
                                        alt=""
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: 280,
                                          borderRadius: 8,
                                          display: "block",
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <input
                      ref={dmFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) sendDmImageMessage(f);
                      }}
                    />
                    <div
                      style={{
                        padding: "14px 20px 18px",
                        borderTop: "1px solid var(--app-border)",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        background: "var(--app-elevated)",
                        flexShrink: 0,
                      }}
                    >
                      <input
                        value={dmInput}
                        onChange={(e) => setDmInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendDmMessage()}
                        placeholder={`Message ${displayPeerLabel(activeDmPeer)}…`}
                        disabled={!connected}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          padding: "12px 16px",
                          borderRadius: 12,
                          border: "1px solid var(--app-border)",
                          background: "var(--app-input-bg)",
                          color: "var(--app-text)",
                          fontSize: 14,
                        }}
                      />
                      <button
                        type="button"
                        onClick={sendDmMessage}
                        disabled={!connected}
                        style={{
                          padding: "12px 22px",
                          borderRadius: 12,
                          border: "none",
                          fontWeight: 600,
                          background: "var(--app-accent)",
                          color: "#fff",
                          cursor: connected ? "pointer" : "default",
                          opacity: connected ? 1 : 0.5,
                        }}
                      >
                        Send
                      </button>
                      <button
                        type="button"
                        onClick={() => dmFileInputRef.current?.click()}
                        disabled={!connected || uploadBusy}
                        style={{
                          padding: "12px 16px",
                          borderRadius: 12,
                          border: "1px solid var(--app-border)",
                          background: "var(--app-input-bg)",
                          color: "var(--app-text)",
                          cursor: connected && !uploadBusy ? "pointer" : "default",
                        }}
                      >
                        {uploadBusy ? "…" : "Image"}
                      </button>
                    </div>
                  </>
                )}
            </div>
          ) : activeNav === "chat" ? (
            <>
              <header
                style={{
                  padding: "14px 20px",
                  borderBottom: "1px solid var(--app-border)",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span>
                  # {activeChannel}{" "}
                  <span style={{ fontWeight: 400, color: "var(--app-muted)", fontSize: 14 }}>
                    — try /me /roll /coin /8ball /choose a,b,c
                  </span>
                </span>
              </header>
              {chatNotice ? (
                <div
                  role="status"
                  style={{
                    margin: "8px 16px 0",
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "rgba(240, 71, 71, 0.12)",
                    border: "1px solid rgba(240, 71, 71, 0.32)",
                    color: "#f04747",
                    fontSize: 14,
                  }}
                >
                  {chatNotice}
                </div>
              ) : null}
              {currentPinned ? (
                <div
                  style={{
                    margin: "0 16px",
                    marginTop: 10,
                    padding: 10,
                    borderRadius: 8,
                    border: "1px solid var(--app-accent)",
                    background: "var(--app-elevated)",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 700, color: "var(--app-accent)", marginBottom: 4 }}>📌 Pinned</div>
                  <strong>{currentPinned.sender}</strong>: {currentPinned.text}
                  {myPerms.pin ? (
                    <button type="button" onClick={unpinChannel} style={{ marginLeft: 12, fontSize: 12, cursor: "pointer" }}>
                      Unpin
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
                <div
                  ref={chatScrollRef}
                  onScroll={onChatScroll}
                  style={{
                    position: "absolute",
                    inset: 0,
                    overflowY: "auto",
                    padding: "12px 16px 80px",
                  }}
                >
                  {currentMessages.map((item) => (
                    <div key={item.id} style={{ marginBottom: 14, fontSize: 14 }}>
                      {item.kind === "system" ? (
                        <div style={{ color: "var(--app-muted)", fontStyle: "italic", fontSize: 13 }}>
                          [{formatSmartTime(item.timestamp)}] {item.text}
                        </div>
                      ) : item.kind === "emote" ? (
                        <div style={{ color: "var(--app-muted)", fontStyle: "italic" }}>
                          [{formatSmartTime(item.timestamp)}] *{item.sender} {lineWithMentions(item.text, onlineLower)}*
                          {item.sender === userName ? (
                            <span>
                              {" "}
                              <button type="button" style={{ fontSize: 11 }} onClick={() => deleteMessage(item.id)}>
                                Delete
                              </button>
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <div>
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <UserAvatar
                              apiBase={apiBase}
                              userName={item.sender}
                              avatarUrl={avatarByUserName.get(item.sender)}
                              size={36}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 8px" }}>
                                <button
                                  type="button"
                                  onClick={() => openUserProfile(item.sender)}
                                  style={{
                                    padding: 0,
                                    border: "none",
                                    background: "none",
                                    font: "inherit",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    color: "var(--app-text)",
                                  }}
                                >
                                  {item.sender}
                                </button>
                                <RolePills roles={memberRolesResolved(item.sender, serverConfig)} max={3} />
                                <span style={{ color: "var(--app-muted)", fontSize: 12 }}>{formatSmartTime(item.timestamp)}</span>
                                {item.editedAt ? (
                                  <span style={{ color: "var(--app-muted)", fontSize: 11 }}> (edited)</span>
                                ) : null}
                              </div>
                              {item.replyTo ? (
                                <div
                                  style={{
                                    borderLeft: "3px solid var(--app-accent)",
                                    paddingLeft: 8,
                                    margin: "6px 0",
                                    color: "var(--app-muted)",
                                    fontSize: 12,
                                  }}
                                >
                                  Replying to {item.replyTo.sender}: {item.replyTo.text}
                                </div>
                              ) : null}
                              {item.text ? (
                                <div style={{ marginTop: 4 }}>{lineWithMentions(item.text, onlineLower)}</div>
                              ) : null}
                              {item.imageUrl ? (
                                <div style={{ marginTop: 6 }}>
                                  <img
                                    src={`${apiBase}${item.imageUrl}`}
                                    alt=""
                                    style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, display: "block" }}
                                  />
                                </div>
                              ) : null}
                              {item.gifUrl ? (
                                <div style={{ marginTop: 6 }}>
                                  <img
                                    src={item.gifUrl}
                                    alt=""
                                    style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, display: "block" }}
                                  />
                                </div>
                              ) : null}
                              {(item.kind === "chat" || item.kind === "emote") && item.id ? (
                                <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                  {["👍", "🔥", "😂", "❤️", "👏", "🎉"].map((emoji) => {
                                    const count = item.reactions?.[emoji]?.length || 0;
                                    const usersList = (item.reactions?.[emoji] || []).join(", ");
                                    return (
                                      <button
                                        key={`${item.id}-${emoji}`}
                                        type="button"
                                        onClick={() => sendReaction(item.id, emoji)}
                                        title={usersList ? `${emoji}: ${usersList}` : `React ${emoji}`}
                                        style={{
                                          border: "1px solid var(--app-border)",
                                          borderRadius: 999,
                                          background: "var(--app-elevated)",
                                          padding: "2px 8px",
                                          cursor: "pointer",
                                          fontSize: 12,
                                          color: "var(--app-text)",
                                        }}
                                      >
                                        {emoji} {count > 0 ? count : ""}
                                      </button>
                                    );
                                  })}
                                  <button
                                    type="button"
                                    style={{ fontSize: 11 }}
                                    onClick={() => {
                                      setReplyTo({ id: item.id, sender: item.sender, text: item.text || "" });
                                      setEditingMessage(null);
                                    }}
                                  >
                                    Reply
                                  </button>
                                  {myPerms.pin ? (
                                    <button type="button" style={{ fontSize: 11 }} onClick={() => pinMessage(item.id)}>
                                      Pin
                                    </button>
                                  ) : null}
                                  {item.sender === userName ? (
                                    <>
                                      <button
                                        type="button"
                                        style={{ fontSize: 11 }}
                                        onClick={() => {
                                          setEditingMessage({ id: item.id });
                                          setMessage(item.text || "");
                                          setReplyTo(null);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button type="button" style={{ fontSize: 11 }} onClick={() => deleteMessage(item.id)}>
                                        Delete
                                      </button>
                                    </>
                                  ) : myPerms.manageMessages ? (
                                    <button type="button" style={{ fontSize: 11 }} onClick={() => deleteMessage(item.id)}>
                                      Delete
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {showJump ? (
                  <button
                    type="button"
                    onClick={scrollChatBottom}
                    style={{
                      position: "absolute",
                      bottom: 88,
                      right: 24,
                      padding: "8px 14px",
                      borderRadius: 20,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-elevated)",
                      color: "var(--app-text)",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    }}
                  >
                    Jump to latest
                  </button>
                ) : null}
              </div>

              {visibleTyping.length > 0 ? (
                <div style={{ padding: "0 16px", fontSize: 13, color: "var(--app-muted)" }}>
                  {visibleTyping.join(", ")} {visibleTyping.length > 1 ? "are" : "is"} typing…
                </div>
              ) : null}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) queueImageForUpload(f);
                }}
              />

              {pendingImageFile ? (
                <div
                  style={{
                    margin: "8px 16px",
                    border: "1px solid var(--app-border)",
                    borderRadius: 8,
                    padding: 8,
                    background: "var(--app-elevated)",
                  }}
                >
                  <p style={{ margin: "0 0 8px", fontSize: 13 }}>
                    Image: <strong>{pendingImageFile.name}</strong>
                  </p>
                  {pendingImagePreviewUrl ? (
                    <img
                      src={pendingImagePreviewUrl}
                      alt="preview"
                      style={{ maxWidth: "100%", maxHeight: 140, borderRadius: 6, display: "block", marginBottom: 8 }}
                    />
                  ) : null}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={() => sendImageMessage(pendingImageFile)} disabled={uploadBusy}>
                      {uploadBusy ? "Uploading…" : "Send image"}
                    </button>
                    <button type="button" onClick={cancelPendingImage} disabled={uploadBusy}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              {replyTo ? (
                <div style={{ padding: "4px 16px", fontSize: 13, color: "var(--app-muted)" }}>
                  Replying to <strong>{replyTo.sender}</strong>: {replyTo.text?.slice(0, 120)}
                  <button type="button" style={{ marginLeft: 8 }} onClick={() => setReplyTo(null)}>
                    ✕
                  </button>
                </div>
              ) : null}
              {editingMessage ? (
                <div style={{ padding: "4px 16px", fontSize: 13, color: "var(--app-accent)" }}>
                  Editing message
                  <button type="button" style={{ marginLeft: 8 }} onClick={() => { setEditingMessage(null); setMessage(""); }}>
                    Cancel
                  </button>
                </div>
              ) : null}

              <div
                style={{
                  padding: 16,
                  borderTop: "1px solid var(--app-border)",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                  background: "var(--app-elevated)",
                }}
              >
                <input
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    const nextTyping = e.target.value.trim().length > 0;
                    sendTypingState(nextTyping);
                    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
                    typingTimerRef.current = setTimeout(() => {
                      sendTypingState(false);
                      typingTimerRef.current = null;
                    }, 1200);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendMessage();
                  }}
                  placeholder={editingMessage ? "Edit message…" : `Message #${activeChannel}`}
                  disabled={!connected}
                  style={{
                    flex: "1 1 200px",
                    minWidth: 0,
                    padding: 12,
                    borderRadius: 8,
                    border: "1px solid var(--app-border)",
                    background: "var(--app-input-bg)",
                    color: "var(--app-text)",
                  }}
                />
                <button type="button" onClick={sendMessage} disabled={!connected}>
                  {editingMessage ? "Save" : "Send"}
                </button>
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!connected || uploadBusy}>
                  Image
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGifPickerOpen((v) => !v);
                    if (!gifPickerOpen) searchGifs(gifQuery || "funny");
                  }}
                  disabled={!connected}
                >
                  GIF
                </button>
              </div>

              {gifPickerOpen ? (
                <div
                  style={{
                    borderTop: "1px solid var(--app-border)",
                    padding: 12,
                    background: "var(--app-elevated)",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                      value={gifQuery}
                      onChange={(e) => setGifQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchGifs(gifQuery)}
                      placeholder="Search GIFs…"
                      style={{ flex: 1, padding: 8, borderRadius: 6, border: "1px solid var(--app-border)" }}
                    />
                    <button type="button" onClick={() => searchGifs(gifQuery)} disabled={gifLoading}>
                      {gifLoading ? "…" : "Search"}
                    </button>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                      gap: 8,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {gifResults.map((gif) => (
                      <button
                        key={gif.id}
                        type="button"
                        onClick={() => sendGifMessage(gif.url)}
                        style={{
                          border: "1px solid var(--app-border)",
                          padding: 0,
                          borderRadius: 6,
                          overflow: "hidden",
                          cursor: "pointer",
                        }}
                      >
                        <img src={gif.url} alt="" style={{ width: "100%", height: 96, objectFit: "cover", display: "block" }} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : activeNav === "arcade" ? (
            <ArcadePanel apiBase={apiBase} sessionToken={sessionToken} userName={userName} />
          ) : activeNav === "economy" ? (
            <EconomyHub apiBase={apiBase} sessionToken={sessionToken} adminToken={websiteAdminToken} />
          ) : activeNav === "servers" ? (
            <div style={{ padding: 24, overflowY: "auto", flex: 1, maxWidth: 560 }}>
              <h2 style={{ marginTop: 0 }}>My servers</h2>
              <p style={{ color: "var(--app-muted)", marginBottom: 20 }}>
                Create a new server or join one with an invite ID. The active server is highlighted below. Website admins (global password) also
                see every server in this list once they unlock admin.
              </p>
              {serversErr ? (
                <p style={{ color: "#f04747" }} role="alert">
                  {serversErr}
                </p>
              ) : null}
              <section
                style={{
                  marginBottom: 24,
                  padding: 16,
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-elevated)",
                }}
              >
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Your memberships</h3>
                {serversBusy && !serverDirectory.length ? (
                  <p style={{ color: "var(--app-muted)" }}>Loading…</p>
                ) : !serverDirectory.length ? (
                  <p style={{ color: "var(--app-muted)" }}>No servers yet. Create one or join below.</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                    {serverDirectory.map((s) => {
                      const meLower = (userName || "").toLowerCase();
                      const ownerLower = (s.owner || "").toLowerCase();
                      const canPic =
                        s.canManageIcon === true ||
                        Boolean(meLower && ownerLower && meLower === ownerLower);
                      const iconSrc = resolveServerIconSrc(apiBase, s.iconUrl);
                      const draftVal =
                        ownerServerIconDraft[s.id] !== undefined ? ownerServerIconDraft[s.id] : s.iconUrl || "";
                      return (
                        <li
                          key={s.id}
                          style={{
                            marginBottom: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            paddingBottom: 12,
                            borderBottom: "1px solid var(--app-border)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt=""
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 10,
                                  objectFit: "cover",
                                  border: "1px solid var(--app-border)",
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <div
                                aria-hidden
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 10,
                                  flexShrink: 0,
                                  background: `hsl(${serverHue(s.id)}, 40%, 42%)`,
                                }}
                              />
                            )}
                            <button
                              type="button"
                              disabled={!connected || s.id === activeGuildId}
                              onClick={() => switchToGuild(s.id)}
                              style={{
                                padding: "8px 12px",
                                borderRadius: 6,
                                border:
                                  s.id === activeGuildId
                                    ? "2px solid var(--app-accent)"
                                    : "1px solid var(--app-border)",
                                background:
                                  s.id === activeGuildId ? "var(--app-accent-muted, rgba(88,101,242,0.15))" : "var(--app-input-bg)",
                                color: "var(--app-text)",
                                cursor: connected && s.id !== activeGuildId ? "pointer" : "default",
                                fontWeight: s.id === activeGuildId ? 700 : 500,
                              }}
                            >
                              {s.name || s.id}
                              {s.id === activeGuildId ? " — active" : ""}
                            </button>
                            {s.allServersVisible ? (
                              <span style={{ fontSize: 11, color: "var(--app-muted)" }}>(all servers)</span>
                            ) : null}
                            <code style={{ fontSize: 11, opacity: 0.75 }}>{s.id}</code>
                          </div>
                          {canPic ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 12, color: "var(--app-muted)", flex: "1 1 120px" }}>Server picture (URL)</span>
                              <input
                                value={draftVal}
                                onChange={(e) =>
                                  setOwnerServerIconDraft((prev) => ({
                                    ...prev,
                                    [s.id]: e.target.value,
                                  }))
                                }
                                placeholder="https://… or clear to remove"
                                style={{
                                  flex: "2 1 220px",
                                  minWidth: 0,
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid var(--app-border)",
                                  background: "var(--app-input-bg)",
                                  color: "var(--app-text)",
                                  fontSize: 13,
                                }}
                              />
                              <button
                                type="button"
                                disabled={serversBusy || draftVal === (s.iconUrl || "")}
                                onClick={() => saveOwnerServerIcon(s.id)}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  border: "none",
                                  background: "var(--app-accent)",
                                  color: "#fff",
                                  fontWeight: 600,
                                  fontSize: 12,
                                  cursor: serversBusy ? "default" : "pointer",
                                  opacity: serversBusy || draftVal === (s.iconUrl || "") ? 0.45 : 1,
                                }}
                              >
                                Save picture
                              </button>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
              <section
                style={{
                  marginBottom: 24,
                  padding: 16,
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-elevated)",
                }}
              >
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Create a server</h3>
                <p style={{ fontSize: 13, color: "var(--app-muted)", marginTop: 0 }}>
                  <strong>Public</strong> servers appear in the left rail for anyone to join. <strong>Private</strong> servers are invite-only (share the server ID).
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <input
                    value={newServerName}
                    onChange={(e) => setNewServerName(e.target.value)}
                    placeholder="Server name"
                    style={{ flex: "1 1 200px", padding: 8, borderRadius: 6, minWidth: 0 }}
                  />
                </div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--app-muted)" }}>
                  Picture URL (optional)
                </label>
                <input
                  value={newServerIconUrl}
                  onChange={(e) => setNewServerIconUrl(e.target.value)}
                  placeholder="https://…"
                  style={{ width: "100%", maxWidth: 480, padding: 8, borderRadius: 6, marginBottom: 12, boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12, fontSize: 13 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="srv-vis-page"
                      checked={newServerVisibility === "private"}
                      onChange={() => setNewServerVisibility("private")}
                    />
                    Private
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="srv-vis-page"
                      checked={newServerVisibility === "public"}
                      onChange={() => setNewServerVisibility("public")}
                    />
                    Public
                  </label>
                </div>
                <button type="button" disabled={serversBusy || !newServerName.trim() || !sessionToken} onClick={createServerFromUi}>
                  Create server
                </button>
              </section>
              <section
                style={{
                  padding: 16,
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-elevated)",
                }}
              >
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Join with server ID</h3>
                <p style={{ fontSize: 13, color: "var(--app-muted)", marginTop: 0 }}>
                  Paste the UUID for a server someone shared with you.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    value={joinServerId}
                    onChange={(e) => setJoinServerId(e.target.value)}
                    placeholder="e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    style={{ flex: "1 1 220px", padding: 8, borderRadius: 6, minWidth: 0 }}
                  />
                  <button type="button" disabled={serversBusy || !joinServerId.trim() || !sessionToken} onClick={joinServerFromUi}>
                    Join
                  </button>
                </div>
              </section>
            </div>
          ) : activeNav === "server-settings" ? (
            <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
              <h2 style={{ marginTop: 0 }}>Server settings</h2>
              <p style={{ color: "var(--app-muted)", marginBottom: 24, maxWidth: 520 }}>
                Create or remove channels, define roles and permissions, and assign roles to members. Global password admins can promote
                server admins from the <strong>Global admin</strong> page (account menu → Global admin dashboard).
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 560 }}>
                {canManageServerPicture && activeGuildId ? (
                  <section
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-elevated)",
                    }}
                  >
                    <h3 style={{ marginTop: 0, fontSize: 16 }}>Server picture</h3>
                    <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--app-muted)" }}>
                      Shown in the server rail. Use an image URL (<code>http(s)://…</code>) or upload a file hosted on this site.
                    </p>
                    <input
                      ref={serverIconFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadServerIconFromFile(f);
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                      {serverSettingsIconPreviewSrc ? (
                        <img
                          src={serverSettingsIconPreviewSrc}
                          alt=""
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 12,
                            objectFit: "cover",
                            border: "1px solid var(--app-border)",
                          }}
                        />
                      ) : (
                        <div
                          aria-hidden
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 12,
                            background: `hsl(${serverHue(activeGuildId)}, 40%, 42%)`,
                          }}
                        />
                      )}
                      <button
                        type="button"
                        disabled={serversBusy}
                        onClick={() => serverIconFileInputRef.current?.click()}
                        style={{ padding: "8px 12px", borderRadius: 6, fontSize: 13 }}
                      >
                        Upload image…
                      </button>
                    </div>
                    <label style={{ display: "block", fontSize: 12, color: "var(--app-muted)", marginBottom: 6 }}>Image URL</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                      <input
                        value={serverSettingsIconDraftVal}
                        onChange={(e) =>
                          setOwnerServerIconDraft((prev) => ({
                            ...prev,
                            [activeGuildId]: e.target.value,
                          }))
                        }
                        placeholder="https://… or /uploads/… after upload"
                        style={{
                          flex: "1 1 220px",
                          minWidth: 0,
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid var(--app-border)",
                          background: "var(--app-input-bg)",
                          color: "var(--app-text)",
                          fontSize: 14,
                        }}
                      />
                      <button
                        type="button"
                        disabled={serversBusy || serverSettingsIconDraftVal === (currentServerMeta?.iconUrl || "")}
                        onClick={() => saveOwnerServerIcon(activeGuildId)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 6,
                          border: "none",
                          background: "var(--app-accent)",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: serversBusy ? "default" : "pointer",
                          opacity: serversBusy || serverSettingsIconDraftVal === (currentServerMeta?.iconUrl || "") ? 0.45 : 1,
                        }}
                      >
                        Save picture
                      </button>
                    </div>
                  </section>
                ) : null}
                {myPerms.purge || myPerms.announce ? (
                  <section
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-elevated)",
                    }}
                  >
                    <h3 style={{ marginTop: 0, fontSize: 16 }}>Moderation tools</h3>
                    <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--app-muted)" }}>
                      Pick a channel for purge or announcements. You need the matching role permission.
                    </p>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: "block", fontSize: 12, color: "var(--app-muted)", marginBottom: 4 }}>Channel</label>
                      <select
                        value={modToolChannel || activeChannel}
                        onChange={(e) => setModToolChannel(e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 6, fontSize: 14, boxSizing: "border-box" }}
                      >
                        {channelsForUi.map((c) => (
                          <option key={`mod-ch-${c.id}`} value={c.id}>
                            #{c.id}
                          </option>
                        ))}
                      </select>
                    </div>
                    {myPerms.purge ? (
                      <div style={{ marginBottom: 14 }}>
                        <button type="button" disabled={!connected} onClick={() => modPurgeSelectedChannel(modToolChannel || activeChannel)}>
                          Purge this channel
                        </button>
                        <span style={{ marginLeft: 10, fontSize: 12, color: "var(--app-muted)" }}>Removes all messages for everyone.</span>
                      </div>
                    ) : null}
                    {myPerms.announce ? (
                      <div>
                        <div style={{ fontSize: 12, color: "var(--app-muted)", marginBottom: 6 }}>Announcement (posts as system message in the channel above)</div>
                        <textarea
                          value={modAnnounceDraft}
                          onChange={(e) => setModAnnounceDraft(e.target.value)}
                          rows={2}
                          placeholder="Short announcement…"
                          style={{ width: "100%", marginBottom: 8, padding: 8, borderRadius: 6, boxSizing: "border-box" }}
                        />
                        <button type="button" disabled={!connected} onClick={() => modSendAnnounceForChannel(modToolChannel || activeChannel)}>
                          Send announcement
                        </button>
                      </div>
                    ) : null}
                  </section>
                ) : null}
                {myPerms.channels ? (
                  <section
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-elevated)",
                    }}
                  >
                    <h3 style={{ marginTop: 0, fontSize: 16 }}>Channels</h3>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: "var(--app-muted)", fontSize: 13, marginBottom: 6 }}>New channel</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <input
                          value={newChannelSlug}
                          onChange={(e) => setNewChannelSlug(e.target.value)}
                          placeholder="channel-name"
                          style={{ flex: "1 1 180px", fontSize: 14, padding: 8, borderRadius: 6, minWidth: 0 }}
                        />
                        <button type="button" disabled={!connected} onClick={createChannelFromUi}>
                          Add channel
                        </button>
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "var(--app-muted)", fontSize: 13, marginBottom: 6 }}>Delete channel</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <select
                          value={deleteChannelPick}
                          onChange={(e) => setDeleteChannelPick(e.target.value)}
                          style={{ flex: "1 1 200px", fontSize: 14, padding: 8, borderRadius: 6, minWidth: 0 }}
                        >
                          <option value="">Select channel…</option>
                          {channelsForUi
                            .filter((c) => c.id !== "general")
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                #{c.id}
                              </option>
                            ))}
                        </select>
                        <button type="button" disabled={!connected || !deleteChannelPick} onClick={deleteChannelFromUi}>
                          Delete channel
                        </button>
                      </div>
                    </div>
                  </section>
                ) : null}
                {myPerms.roles ? (
                  <section
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      border: "1px solid var(--app-border)",
                      background: "var(--app-elevated)",
                    }}
                  >
                    <h3 style={{ marginTop: 0, fontSize: 16 }}>Roles</h3>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ color: "var(--app-muted)", fontSize: 13, marginBottom: 8 }}>Create role</div>
                      <input
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        placeholder="Role name"
                        style={{ width: "100%", fontSize: 14, marginBottom: 8, padding: 8, borderRadius: 6, boxSizing: "border-box" }}
                      />
                      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        Color{" "}
                        <input type="color" value={newRoleColor} onChange={(e) => setNewRoleColor(e.target.value)} />
                      </label>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                          gap: 8,
                          marginBottom: 12,
                        }}
                      >
                        {[
                          ["pin", "Pin messages"],
                          ["kick", "Kick members"],
                          ["channels", "Channels"],
                          ["roles", "Manage roles"],
                          ["manageMessages", "Delete msgs"],
                          ["purge", "Purge channel"],
                          ["announce", "Announce"],
                        ].map(([key, lab]) => (
                          <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={!!newRolePerms[key]}
                              onChange={(e) =>
                                setNewRolePerms((p) => ({
                                  ...p,
                                  [key]: e.target.checked,
                                }))
                              }
                            />
                            {lab}
                          </label>
                        ))}
                      </div>
                      <button type="button" disabled={!connected} onClick={createRoleFromUi}>
                        Create role
                      </button>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ color: "var(--app-muted)", fontSize: 13, marginBottom: 8 }}>Existing roles</div>
                      <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 200, overflowY: "auto" }}>
                        {(serverConfig?.roles || []).map((r) => (
                          <li key={r.id} style={{ marginBottom: 8 }}>
                            <span style={{ color: r.color || "#5865F2" }}>●</span> {r.name}
                            <button type="button" style={{ marginLeft: 8, fontSize: 12 }} onClick={() => deleteRoleById(r.id)}>
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div style={{ color: "var(--app-muted)", fontSize: 13, marginBottom: 8 }}>Assign or remove role</div>
                      <input
                        value={assignRoleUser}
                        onChange={(e) => setAssignRoleUser(e.target.value)}
                        placeholder="Registered username"
                        style={{ width: "100%", fontSize: 14, marginBottom: 8, padding: 8, borderRadius: 6, boxSizing: "border-box" }}
                      />
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <select
                          value={assignRoleId}
                          onChange={(e) => setAssignRoleId(e.target.value)}
                          style={{ flex: "1 1 160px", fontSize: 14, padding: 8, borderRadius: 6, minWidth: 0 }}
                        >
                          <option value="">Choose role…</option>
                          {(serverConfig?.roles || []).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                        <button type="button" disabled={!connected} onClick={assignRoleFromUi}>
                          Assign
                        </button>
                        <button type="button" disabled={!connected} onClick={unassignRoleFromUi}>
                          Remove from user
                        </button>
                      </div>
                    </div>
                  </section>
                ) : null}
                {!myPerms.channels && !myPerms.roles && !canManageServerPicture ? (
                  <p style={{ color: "var(--app-muted)" }}>You do not have permission to change server settings.</p>
                ) : null}
              </div>
            </div>
          ) : activeNav === "polls" ? (
            <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
              <h2 style={{ marginTop: 0 }}>Poll feed</h2>
              <p style={{ color: "var(--app-muted)" }}>Create polls and vote together.</p>
              <input
                value={newPollQuestion}
                onChange={(e) => setNewPollQuestion(e.target.value)}
                placeholder="Poll question"
                disabled={!connected}
                style={{ width: "100%", marginBottom: 8, padding: 8, borderRadius: 6 }}
              />
              {newPollOptions.map((opt, idx) => (
                <input
                  key={`poll-opt-${idx}`}
                  value={opt}
                  onChange={(e) => {
                    setNewPollOptions((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)));
                  }}
                  placeholder={`Option ${idx + 1}`}
                  disabled={!connected}
                  style={{ width: "100%", marginBottom: 6, padding: 8, borderRadius: 6 }}
                />
              ))}
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setNewPollOptions((prev) => (prev.length < 6 ? [...prev, ""] : prev))}
                  disabled={!connected || newPollOptions.length >= 6}
                >
                  + Option
                </button>
                <button
                  type="button"
                  onClick={() => setNewPollOptions((prev) => (prev.length > 2 ? prev.slice(0, -1) : prev))}
                  disabled={!connected || newPollOptions.length <= 2}
                >
                  − Option
                </button>
                <button type="button" onClick={createPoll} disabled={!connected}>
                  Create poll
                </button>
              </div>
              {pollsState.length > 0 ? (
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", marginBottom: 16 }}>
                  {pollsState.map((poll) => (
                    <div
                      key={poll.id}
                      style={{
                        border: "1px solid var(--app-border)",
                        borderRadius: 8,
                        padding: 12,
                        background: "var(--app-elevated)",
                      }}
                    >
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>{poll.question}</strong>
                      </p>
                      {poll.options.map((option) => {
                        const totalVotes = poll.totalVotes || 0;
                        const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                        return (
                          <div key={option.id} style={{ marginBottom: 8 }}>
                            <button
                              type="button"
                              onClick={() => votePoll(poll.id, option.id)}
                              disabled={!connected}
                              style={{ width: "100%", textAlign: "left", padding: 8 }}
                            >
                              {option.text} — {option.votes} votes ({pct}%)
                            </button>
                          </div>
                        );
                      })}
                      <small style={{ color: "var(--app-muted)" }}>
                        By {poll.createdBy} · {poll.totalVotes} votes
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "var(--app-muted)" }}>No active polls.</p>
              )}
              <h3 style={{ fontSize: 16 }}>Recent activity</h3>
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {pollFeedItems.length === 0 ? (
                  <p style={{ color: "var(--app-muted)" }}>None yet.</p>
                ) : (
                  pollFeedItems.map((feed) => (
                    <div key={feed.id} style={{ borderTop: "1px solid var(--app-border)", paddingTop: 8, marginTop: 8 }}>
                      <strong>{feed.event === "created" ? "New poll" : "Vote"}</strong> by {feed.by}
                      <div>{feed.question}</div>
                      <div style={{ fontSize: 13, color: "var(--app-muted)" }}>
                        {feed.options?.map((o) => `${o.text}: ${o.votes}`).join(" | ")}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: 16, overflowY: "auto", flex: 1, display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              <div style={{ border: "1px solid var(--app-border)", borderRadius: 8, padding: 12, background: "var(--app-elevated)" }}>
                <h3 style={{ marginTop: 0 }}>Group board</h3>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addTodo()}
                    placeholder="Add a task"
                    disabled={!connected}
                    style={{ flex: 1, padding: 8 }}
                  />
                  <button type="button" onClick={addTodo} disabled={!connected}>
                    Add
                  </button>
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {sharedTodos.length === 0 ? (
                    <p style={{ color: "var(--app-muted)", margin: 0 }}>No tasks.</p>
                  ) : (
                    sharedTodos.map((todo) => (
                      <div key={todo.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <input type="checkbox" checked={Boolean(todo.done)} onChange={() => toggleTodo(todo.id)} disabled={!connected} />
                        <span style={{ flex: 1, textDecoration: todo.done ? "line-through" : "none", color: todo.done ? "var(--app-muted)" : "inherit" }}>
                          {todo.text}
                        </span>
                        <small style={{ color: "var(--app-muted)" }}>{todo.createdBy}</small>
                        <button type="button" onClick={() => removeTodo(todo.id)} disabled={!connected}>
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div style={{ border: "1px solid var(--app-border)", borderRadius: 8, padding: 12, background: "var(--app-elevated)" }}>
                <h3 style={{ marginTop: 0 }}>Idea board</h3>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input
                    value={newIdeaText}
                    onChange={(e) => setNewIdeaText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addIdea()}
                    placeholder="Suggest something…"
                    disabled={!connected}
                    style={{ flex: 1, padding: 8 }}
                  />
                  <button type="button" onClick={addIdea} disabled={!connected}>
                    Add
                  </button>
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {ideaBoard.length === 0 ? (
                    <p style={{ color: "var(--app-muted)", margin: 0 }}>No ideas yet.</p>
                  ) : (
                    ideaBoard.map((idea) => (
                      <div key={idea.id} style={{ borderTop: "1px solid var(--app-border)", paddingTop: 8, marginTop: 8 }}>
                        <div style={{ fontWeight: 600 }}>{idea.text}</div>
                        <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                          <small style={{ color: "var(--app-muted)" }}>by {idea.by}</small>
                          <button type="button" onClick={() => voteIdea(idea.id)} disabled={!connected}>
                            ▲ {idea.votes}
                          </button>
                          {idea.by === userName ? (
                            <button type="button" onClick={() => removeIdea(idea.id)} disabled={!connected}>
                              Remove
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Members */}
        <aside
          style={{
            width: 220,
            flexShrink: 0,
            borderLeft: "1px solid var(--app-border)",
            background: "var(--app-sidebar)",
            padding: 12,
            overflowY: "auto",
          }}
        >
          <div style={{ fontSize: 11, textTransform: "uppercase", color: "var(--app-muted)", marginBottom: 8 }}>
            Online — {presence.length}
          </div>
          {presence
            .slice()
            .sort((a, b) => (a.displayName || a.userName).localeCompare(b.displayName || b.userName))
            .map((p) => (
              <div
                key={p.userName}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  marginBottom: 12,
                  padding: "8px 6px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
                role="presentation"
                onClick={() => openUserProfile(p.userName)}
              >
                <UserAvatar apiBase={apiBase} userName={p.userName} avatarUrl={p.avatarUrl} size={36} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openUserProfile(p.userName);
                    }}
                    style={{
                      display: "block",
                      padding: 0,
                      border: "none",
                      background: "none",
                      font: "inherit",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "var(--app-text)",
                      cursor: "pointer",
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {p.displayName || p.userName}
                  </button>
                  <div style={{ marginTop: 4 }}>
                    <RolePills roles={memberRolesResolved(p.userName, serverConfig)} max={2} />
                  </div>
                  {isUserServerAdmin(p.userName) ? (
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: 4,
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: 0.05,
                        padding: "2px 6px",
                        borderRadius: 3,
                        background: "rgba(240, 178, 50, 0.18)",
                        color: "#f0b232",
                        border: "1px solid rgba(240, 178, 50, 0.4)",
                      }}
                    >
                      ADMIN
                    </span>
                  ) : null}
                  {p.status ? (
                    <div style={{ fontSize: 11, color: "var(--app-muted)", overflow: "hidden", textOverflow: "ellipsis", marginTop: 4 }}>
                      {p.status}
                    </div>
                  ) : p.channel ? (
                    <div style={{ fontSize: 11, color: "var(--app-muted)", marginTop: 4 }}>#{p.channel}</div>
                  ) : null}
                  {p.userName !== userName ? (
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDmWith(p.userName);
                        }}
                        style={{
                          fontSize: 11,
                          padding: "2px 0",
                          border: "none",
                          background: "none",
                          color: "var(--app-accent)",
                          cursor: "pointer",
                        }}
                      >
                        Message
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openUserProfile(p.userName);
                        }}
                        style={{
                          fontSize: 11,
                          padding: "2px 0",
                          border: "none",
                          background: "none",
                          color: "var(--app-muted)",
                          cursor: "pointer",
                        }}
                      >
                        Profile
                      </button>
                      {myPerms.kick && !isUserServerAdmin(p.userName) ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            modKickUser(p.userName);
                          }}
                          style={{
                            fontSize: 11,
                            padding: "2px 0",
                            border: "none",
                            background: "none",
                            color: "#ed4245",
                            cursor: "pointer",
                          }}
                        >
                          Kick
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
        </aside>
      </div>

      {profileModalUser ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 950,
            padding: 20,
          }}
          role="presentation"
          onClick={() => setProfileModalUser(null)}
        >
          <div
            role="dialog"
            aria-labelledby="profile-modal-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--app-elevated)",
              borderRadius: 16,
              padding: 24,
              maxWidth: 420,
              width: "100%",
              border: "1px solid var(--app-border)",
              color: "var(--app-text)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{ display: "flex", gap: 18, alignItems: "flex-start", marginBottom: 20 }}>
              <UserAvatar
                apiBase={apiBase}
                userName={profileModalUser}
                avatarUrl={avatarByUserName.get(profileModalUser)}
                size={80}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 id="profile-modal-title" style={{ margin: "0 0 6px", fontSize: 22, lineHeight: 1.2 }}>
                  {profileModalUser}
                </h2>
                {(() => {
                  const pr = presenceByUserName.get(profileModalUser);
                  const label = pr?.displayName && pr.displayName !== profileModalUser ? pr.displayName : null;
                  return label ? (
                    <div style={{ fontSize: 14, color: "var(--app-muted)", marginBottom: 8 }}>Display name: {label}</div>
                  ) : null;
                })()}
                {isUserServerAdmin(profileModalUser) ? (
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0.06,
                      padding: "4px 10px",
                      borderRadius: 6,
                      background: "rgba(240, 178, 50, 0.2)",
                      color: "#f0b232",
                      border: "1px solid rgba(240, 178, 50, 0.45)",
                      marginBottom: 12,
                    }}
                  >
                    SERVER ADMIN
                  </span>
                ) : null}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--app-muted)", marginBottom: 6, textTransform: "uppercase" }}>
                    Roles
                  </div>
                  {memberRolesResolved(profileModalUser, serverConfig).length ? (
                    <RolePills roles={memberRolesResolved(profileModalUser, serverConfig)} />
                  ) : (
                    <span style={{ fontSize: 13, color: "var(--app-muted)" }}>No roles assigned</span>
                  )}
                </div>
              </div>
            </div>
            {(() => {
              const pr = presenceByUserName.get(profileModalUser);
              if (!pr) {
                return (
                  <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--app-muted)" }}>
                    Not online right now — profile details may be limited.
                  </p>
                );
              }
              return (
                <div style={{ marginBottom: 20, fontSize: 14 }}>
                  {pr.status ? (
                    <p style={{ margin: "0 0 8px" }}>
                      <strong style={{ color: "var(--app-muted)" }}>Status</strong> — {pr.status}
                    </p>
                  ) : null}
                  {pr.channel ? (
                    <p style={{ margin: 0 }}>
                      <strong style={{ color: "var(--app-muted)" }}>In channel</strong> #{pr.channel}
                    </p>
                  ) : null}
                  {!pr.status && !pr.channel ? (
                    <p style={{ margin: 0, color: "var(--app-muted)" }}>Online</p>
                  ) : null}
                </div>
              );
            })()}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {profileModalUser !== userName ? (
                <button
                  type="button"
                  onClick={() => {
                    openDmWith(profileModalUser);
                    setActiveNav("dms");
                    setProfileModalUser(null);
                  }}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: "none",
                    fontWeight: 600,
                    background: "var(--app-accent)",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Message
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setProfileModalUser(null)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-input-bg)",
                  color: "var(--app-text)",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createServerModalOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 950,
            padding: 16,
          }}
          role="presentation"
          onClick={() => setCreateServerModalOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setCreateServerModalOpen(false)}
        >
          <div
            style={{
              background: "var(--app-elevated)",
              borderRadius: 12,
              padding: 22,
              maxWidth: 400,
              width: "100%",
              border: "1px solid var(--app-border)",
              color: "var(--app-text)",
            }}
            role="dialog"
            aria-labelledby="create-srv-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="create-srv-title" style={{ marginTop: 0 }}>
              Create a server
            </h3>
            <p style={{ fontSize: 13, color: "var(--app-muted)", marginTop: 0 }}>
              Public servers show in the server list for everyone. Private servers need an invite link (server ID).
            </p>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--app-muted)" }}>
              Server name
            </label>
            <input
              value={newServerName}
              onChange={(e) => setNewServerName(e.target.value)}
              placeholder="My cool server"
              style={{
                width: "100%",
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--app-border)",
                background: "var(--app-input-bg)",
                color: "var(--app-text)",
                boxSizing: "border-box",
              }}
            />
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--app-muted)" }}>
              Picture URL (optional)
            </label>
            <input
              value={newServerIconUrl}
              onChange={(e) => setNewServerIconUrl(e.target.value)}
              placeholder="https://… or leave blank"
              style={{
                width: "100%",
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--app-border)",
                background: "var(--app-input-bg)",
                color: "var(--app-text)",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, fontSize: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="srv-vis-modal"
                  checked={newServerVisibility === "private"}
                  onChange={() => setNewServerVisibility("private")}
                />
                Private — invite only
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="srv-vis-modal"
                  checked={newServerVisibility === "public"}
                  onChange={() => setNewServerVisibility("public")}
                />
                Public — listed in the sidebar
              </label>
            </div>
            {serversErr ? (
              <p style={{ color: "#f04747", fontSize: 13, margin: "0 0 12px" }} role="alert">
                {serversErr}
              </p>
            ) : null}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setCreateServerModalOpen(false);
                  setServersErr("");
                  setNewServerIconUrl("");
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "1px solid var(--app-border)",
                  background: "transparent",
                  color: "var(--app-text)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="auth-gate-submit"
                style={{ width: "auto", margin: 0, padding: "10px 18px" }}
                disabled={serversBusy || !newServerName.trim()}
                onClick={createServerFromUi}
              >
                {serversBusy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {arcadeSocialToast ? (
        <div className="arcade-social-toast" role="status">
          {arcadeSocialToast}
        </div>
      ) : null}
    </div>
  );
}

export default App;
